/**
 * Google My Business API Service
 * This service handles interactions with the Google My Business API
 * using the client ID and secret from the .env file
 */

import logger from '../utils/logger';
import oauthConfig from '../config/oauth';
import tokenManager from '../auth/TokenManager';
import { registerGoogleRefresher } from '../auth/googleRefresher';
import AutoTokenManager from '../utils/autoTokenUtils';
import sessionManager from '../utils/sessionManager';

class GMBService {
  constructor() {
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    this.clientSecret = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;
    this.projectId = process.env.REACT_APP_GMB_PROJECT_ID;
    this.apiKey = process.env.REACT_APP_GMB_API_KEY;
    this._adminTokenCache = { userId: null, token: null, fetchedAt: 0 };

    // Log configuration for debugging (without exposing secrets)
    if (process.env.REACT_APP_DEBUG_MODE === 'true') {
      logger.debug('GMBService Configuration:', {
        hasClientId: !!this.clientId,
        hasClientSecret: !!this.clientSecret,
        hasProjectId: !!this.projectId,
        hasApiKey: !!this.apiKey,
        projectId: this.projectId || '(not set - will work without it)'
      });
    }

    // Register refresher once
    registerGoogleRefresher();
  }

  /**
   * Get Business Profile attributes for a specific location
   * Uses Business Information API v1: locations/{locationId}/attributes
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Array>} Array of attribute objects
   */
  async getLocationAttributes(locationNameOrId, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      const url = `https://mybusinessbusinessinformation.googleapis.com/v1/${locName}/attributes`;
      const response = await this._googleFetch(url, { method: 'GET' }, accessToken);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to fetch location attributes');
      }
      const data = await response.json();
      // Response shape: { attributes: [ { attributeId, valueType, values, ... } ] }
      return Array.isArray(data?.attributes) ? data.attributes : [];
    } catch (error) {
      logger.error('Error fetching location attributes:', error);
      return [];
    }
  }

  _getAdminAccessData() {
    try {
      const raw = sessionStorage.getItem('adminAccessData');
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      logger.warn('[GMBService] Failed to parse adminAccessData:', error);
      return null;
    }
  }

  _getAdminGoogleToken() {
    try {
      const cached = sessionStorage.getItem('adminResolvedGoogleToken');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.token) {
          const adminViewingUserId = this._resolveAdminViewingUserId();
          if (!parsed.userId || !adminViewingUserId || String(parsed.userId) === String(adminViewingUserId)) {
            return parsed.token;
          }
        }
      }
    } catch (_) { }

    const adminAccess = this._getAdminAccessData();
    if (adminAccess?.googleAccessToken) {
      return adminAccess.googleAccessToken;
    }
    try {
      const token = sessionStorage.getItem('googleAccessToken');
      if (token) return token;
    } catch (_) { }
    try {
      const token = localStorage.getItem('googleAccessToken');
      if (token) return token;
    } catch (_) { }
    return null;
  }

  _getAdminAccountsFromSession() {
    const adminAccess = this._getAdminAccessData();
    if (!adminAccess?.gmbAccounts?.length) return [];

    const byAccount = new Map();
    adminAccess.gmbAccounts.forEach((entry) => {
      const accountId = entry.account_id || entry.accountId;
      if (!accountId) return;
      if (!byAccount.has(accountId)) {
        byAccount.set(accountId, {
          name: `accounts/${accountId}`,
          accountId,
          accountName: entry.account_name || adminAccess.targetUserName || 'Client Account',
          adminSource: true
        });
      }
    });

    return Array.from(byAccount.values());
  }

  _getAdminLocationsForAccount(accountNameOrId) {
    const adminAccess = this._getAdminAccessData();
    if (!adminAccess?.gmbAccounts?.length) return [];

    const targetAccountId = String(accountNameOrId || '')
      .replace(/^accounts\//, '')
      .trim();
    if (!targetAccountId) return [];

    return adminAccess.gmbAccounts
      .filter((entry) => String(entry.account_id || entry.accountId) === targetAccountId)
      .map((entry) => {
        let parsedLocation = entry.location_data;
        if (typeof parsedLocation === 'string') {
          try {
            parsedLocation = JSON.parse(parsedLocation);
          } catch (_) {
            parsedLocation = null;
          }
        }
        const locationName =
          parsedLocation?.name ||
          (entry.location_id ? `locations/${entry.location_id}` : null);
        return {
          ...(parsedLocation || {}),
          name: locationName,
          adminSource: true,
          accountId: entry.account_id || entry.accountId,
          locationId: entry.location_id
        };
      })
      .filter(Boolean);
  }

  /**
   * Resolve which user's data the admin is currently viewing.
   * Tries (in order): explicit sessionStorage flag, adminAccessData payload,
   * URL query params (userId/tempKey). Whenever we discover a user id from
   * the URL we persist it back to sessionStorage so downstream calls stay in sync.
   */
  _resolveAdminViewingUserId() {
    let adminViewingUserId = null;

    try {
      adminViewingUserId = sessionStorage.getItem('adminViewingUserId');
    } catch (_) {
      // ignore storage access issues
    }

    if (!adminViewingUserId) {
      const adminAccess = this._getAdminAccessData();
      if (adminAccess?.targetUserId) {
        adminViewingUserId = String(adminAccess.targetUserId);
      }
    }

    if (!adminViewingUserId && typeof window !== 'undefined') {
      try {
        const params = new URLSearchParams(window.location?.search || '');
        const queryUserId = params.get('userId');
        logger.log(`🔍 [GMBService] _resolveAdminViewingUserId - URL search: "${window.location?.search}", queryUserId: "${queryUserId}"`);
        if (queryUserId) {
          adminViewingUserId = queryUserId;
          logger.log(`✅ [GMBService] Found adminViewingUserId from URL: ${adminViewingUserId}`);
        } else {
          const tempKey = params.get('tempKey');
          if (tempKey) {
            let tempPayload = null;
            try {
              tempPayload = sessionStorage.getItem(tempKey);
            } catch (_) {
              // ignore
            }
            if (!tempPayload) {
              try {
                tempPayload = localStorage.getItem(tempKey);
              } catch (_) {
                // ignore
              }
            }
            if (tempPayload) {
              try {
                const parsed = JSON.parse(tempPayload);
                if (parsed?.targetUserId) {
                  adminViewingUserId = String(parsed.targetUserId);
                  if (!sessionStorage.getItem('adminAccessData')) {
                    sessionStorage.setItem('adminAccessData', tempPayload);
                  }
                }
              } catch (e) {
                logger.warn('[GMBService] Failed to parse admin temp payload:', e);
              }
              try {
                sessionStorage.removeItem(tempKey);
              } catch (_) { }
              try {
                localStorage.removeItem(tempKey);
                const pointer = localStorage.getItem('latestAdminAccessTempKey');
                if (pointer === tempKey) {
                  localStorage.removeItem('latestAdminAccessTempKey');
                }
              } catch (_) { }
            }
          }
        }

        if (adminViewingUserId) {
          try {
            sessionStorage.setItem('adminViewingUserId', adminViewingUserId);
          } catch (_) {
            // ignore persistence failures
          }
        }
      } catch (error) {
        logger.warn('[GMBService] Unable to resolve admin viewing context from URL:', error);
      }
    }

    return adminViewingUserId;
  }

  // Internal: get a valid access token, optionally using provided token
  async _getAccessToken(providedToken, forceRefresh = false) {
    // If admin is viewing a client, resolve token through backend-managed credentials
    const adminViewingUserId = this._resolveAdminViewingUserId();
    if (adminViewingUserId) {
      const now = Date.now();

      if (!forceRefresh && this._adminTokenCache.userId === adminViewingUserId && this._adminTokenCache.token && now - this._adminTokenCache.fetchedAt < 45 * 60 * 1000) {
        return this._adminTokenCache.token;
      }

      if (!forceRefresh) {
        const adminToken = this._getAdminGoogleToken();
        if (adminToken) {
          this._adminTokenCache = { userId: adminViewingUserId, token: adminToken, fetchedAt: now };
          return adminToken;
        }
      }

      try {
        const ApiService = (await import('./api')).default;
        const endpoint = forceRefresh
          ? `/gmb/token?forceRefresh=true`
          : '/gmb/token';
        const response = await ApiService.get(endpoint);
        const resolvedToken =
          response?.data?.accessToken ||
          response?.accessToken ||
          response?.data?.token ||
          response?.token;

        if (resolvedToken) {
          this._adminTokenCache = { userId: adminViewingUserId, token: resolvedToken, fetchedAt: Date.now() };
          try {
            sessionStorage.setItem('adminResolvedGoogleToken', JSON.stringify({ token: resolvedToken, userId: adminViewingUserId, fetchedAt: Date.now() }));
          } catch (_) { }
          logger.log(`🔑 [GMBService] Resolved admin token via backend for user ${adminViewingUserId}`);
          return resolvedToken;
        }

        logger.warn(`[GMBService] Backend did not return a Google token for admin view of user ${adminViewingUserId}`);
      } catch (error) {
        logger.warn(`[GMBService] Failed to resolve admin Google token via backend for user ${adminViewingUserId}:`, error);
      }

      return null;
    }

    if (providedToken && !forceRefresh) return providedToken;

    // Try auto Google token first if not forcing refresh
    if (!forceRefresh) {
      const autoToken = AutoTokenManager.getAutoGoogleToken();
      if (autoToken) {
        return autoToken;
      }
    }

    // Fallback to TokenManager with optional force refresh
    return await tokenManager.getValidAccessToken('google', forceRefresh);
  }

  // Public method to get access token (for external services)
  async getAccessToken(providedToken, forceRefresh = false) {
    return this._getAccessToken(providedToken, forceRefresh);
  }

  // Internal: fetch helper that attaches token and retries once on 401 after refresh
  async _googleFetch(url, options = {}, accessToken) {
    let token = await this._getAccessToken(accessToken);
    if (!token) {
      token = await this._getAccessToken(accessToken, true);
      if (!token) {
        logger.warn('[GMBService] Unable to resolve Google access token for request:', url);
        throw new Error('Missing Google access token. Please reconnect the account.');
      }
    }

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };

    // Only include X-Goog-User-Project if project ID is configured and valid
    // This header requires specific Google Cloud permissions
    if (this.projectId && this.projectId !== 'undefined' && this.projectId.trim()) {
      headers['X-Goog-User-Project'] = this.projectId;
    }

    // Add a timeout using AbortController to prevent hanging requests
    const controller = new AbortController();
    const timeoutMs = typeof options.timeoutMs === 'number' && options.timeoutMs > 0 ? options.timeoutMs : 20000;
    const timer = setTimeout(() => controller.abort('timeout'), timeoutMs);
    const { timeoutMs: _omitTimeoutMs, ...restOptions } = options;

    let res;
    try {
      res = await fetch(url, { ...restOptions, headers, signal: controller.signal });
    } catch (e) {
      if (e && (e.name === 'AbortError' || String(e).includes('timeout'))) {
        logger.warn(`[GMBService] _googleFetch timeout after ${timeoutMs}ms:`, url);
      }
      throw e;
    } finally {
      clearTimeout(timer);
    }

    // Handle specific permission errors for X-Goog-User-Project
    if (res.status === 403 && this.projectId) {
      logger.warn('Permission denied with X-Goog-User-Project header. Retrying without it...');
      const headersWithoutProject = { ...headers };
      delete headersWithoutProject['X-Goog-User-Project'];
      // Retry with same timeout guard
      const controller2 = new AbortController();
      const timer2 = setTimeout(() => controller2.abort('timeout'), timeoutMs);
      try {
        res = await fetch(url, { ...restOptions, headers: headersWithoutProject, signal: controller2.signal });
      } finally {
        clearTimeout(timer2);
      }
    }

    if (res.status === 401) {
      // try refresh using our unified token resolver (handles admin + user contexts)
      const refreshedToken = await this._getAccessToken(accessToken, true);
      if (!refreshedToken) {
        logger.warn('[GMBService] _googleFetch received 401 but token refresh failed; returning original response.');
        return res;
      }
      token = refreshedToken;
      const retryHeaders = {
        ...headers,
        Authorization: `Bearer ${token}`,
      };
      // Remove project header if it caused issues before
      if (this.projectId && headers['X-Goog-User-Project']) {
        delete retryHeaders['X-Goog-User-Project'];
      }
      const controller3 = new AbortController();
      const timer3 = setTimeout(() => controller3.abort('timeout'), timeoutMs);
      try {
        res = await fetch(url, { ...restOptions, headers: retryHeaders, signal: controller3.signal });
      } finally {
        clearTimeout(timer3);
      }

      // If still 401 after retry, DO NOT trigger session expired immediately.
      // Let the calling code (e.g., backend proxy fallback) handle it gracefully.
      if (res.status === 401) {
        logger.warn('[GMBService] Google token expired and refresh failed. Caller should fallback to backend proxy.');
        // Do NOT call sessionManager.triggerSessionExpired() here; caller will decide
      }
    }
    return res;
  }

  /**
   * Get media (photos and videos) for a specific location
   * Uses GMB v4 media endpoint.
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountId - Account ID string (e.g., '12345')
   * @param {string} locationId - Location ID string (e.g., '67890')
   * @returns {Promise<Array>} List of media items
   */
  async getMedia(accessToken, accountId, locationId, pageSize = 50) {
    try {
      if (!accountId || !locationId) {
        throw new Error('Invalid accountId or locationId');
      }

      // Use backend proxy to avoid CORS and Auth issues with deprecated v4 API from frontend
      // The backend will handle the GMB API interaction (v4 or replacement)
      const cleanAccountId = String(accountId).replace('accounts/', '');
      const cleanLocationId = String(locationId).replace('locations/', '');

      const ApiService = (await import('./api')).default;
      const response = await ApiService.get(`/gmb/media/${cleanAccountId}/${cleanLocationId}`);

      const items = response?.data?.mediaItems || response?.mediaItems || [];
      return items;
    } catch (error) {
      logger.error('Error fetching media:', error);
      throw error;
    }
  }

  /**
   * Get total photo count for a specific location using paginated media endpoint
   * Counts only PHOTO items (filtering by mediaFormat === 'PHOTO' when present)
   * @param {string} accessToken
   * @param {string} accountId
   * @param {string} locationId
   * @param {number} pageSize - items per page (default 50)
   * @returns {Promise<number>} Total count of photos
   */
  async getMediaCount(accessToken, accountId, locationId, pageSize = 50) {
    try {
      if (!accountId || !locationId) {
        throw new Error('Invalid accountId or locationId');
      }

      let total = 0;
      let nextPageToken = null;

      const cleanAccountId = String(accountId).replace('accounts/', '');
      const cleanLocationId = String(locationId).replace('locations/', '');

      do {
        const url = `https://mybusiness.googleapis.com/v4/accounts/${cleanAccountId}/locations/${cleanLocationId}/media` +
          `?pageSize=${encodeURIComponent(pageSize)}` + (nextPageToken ? `&pageToken=${encodeURIComponent(nextPageToken)}` : '');

        const response = await this._googleFetch(
          url,
          { method: 'GET' },
          accessToken
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error?.message || 'Failed to fetch media count');
        }

        const data = await response.json();
        const items = Array.isArray(data.mediaItems) ? data.mediaItems : [];
        // Count photo-only
        total += items.filter(i => (i.mediaFormat === 'PHOTO' || !i.mediaFormat)).length;
        nextPageToken = data.nextPageToken || null;

        if (total > 50000) {
          logger.warn('[GMBService] Media count exceeded 50k items, stopping count early');
          break;
        }
      } while (nextPageToken);

      return total;
    } catch (error) {
      logger.error('Error fetching media count:', error);
      throw error;
    }
  }

  /**
   * Upload video to a specific location
   * Uses GMB v4 media endpoint.
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountId - Account ID string (e.g., '12345')
   * @param {string} locationId - Location ID string (e.g., '67890')
   * @param {File} videoFile - Video file to upload
   * @returns {Promise<Object>} Upload result
   */
  async uploadVideo(accessToken, accountId, locationId, videoFile) {
    try {
      if (!accountId || !locationId) {
        throw new Error('Invalid accountId or locationId');
      }

      if (!videoFile) {
        throw new Error('Video file is required');
      }

      // Validate file type
      const allowedTypes = ['video/mp4', 'video/mov', 'video/avi'];
      if (!allowedTypes.includes(videoFile.type)) {
        throw new Error('Invalid video format. Only MP4, MOV, and AVI files are supported.');
      }

      // Validate file size (100MB limit)
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      if (videoFile.size > maxSize) {
        throw new Error('Video file size must be less than 100MB');
      }

      // GMB API v4 has CORS restrictions on direct uploads for videos too
      // Videos are more complex and larger files, so we'll handle them similarly to photos

      logger.log('[uploadVideo] Video upload requested');
      logger.warn('[uploadVideo] Direct video upload not supported due to CORS and file size limitations');

      // Return a placeholder result indicating video upload is not directly supported
      const result = {
        name: `video_placeholder_${Date.now()}`,
        mediaFormat: 'VIDEO',
        description: videoFile.name,
        createTime: new Date().toISOString(),
        locationAssociation: {
          category: 'PROFILE'
        },
        error: 'Direct video upload not supported. Please use a video hosting service (YouTube, Vimeo) and add the link to your business profile.'
      };

      logger.log('[uploadVideo] Video upload not directly supported - recommend using video hosting service');

      // Throw an error with helpful message
      throw new Error(
        'Video uploads require a video hosting service. Please:\n\n' +
        '1. Upload your video to YouTube or Vimeo\n' +
        '2. Add the video link to your Google Business Profile\n' +
        '3. Or contact support for assistance with video uploads'
      );
    } catch (error) {
      logger.error('Error uploading video:', error);
      throw error;
    }
  }

  /**
   * Upload media (photo) to a specific location
   * Uses GMB v4 media endpoint.
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountId - Account ID string (e.g., '12345')
   * @param {string} locationId - Location ID string (e.g., '67890')
   * @param {File} imageFile - Image file to upload
   * @param {string} category - Optional category ('PROFILE', 'COVER', 'ADDITIONAL', etc.)
   * @returns {Promise<Object>} Upload result with media item containing sourceUrl
   */
  async uploadMedia(accessToken, accountId, locationId, imageFile, category = 'ADDITIONAL') {
    try {
      if (!accountId || !locationId) {
        throw new Error('Invalid accountId or locationId');
      }

      if (!imageFile) {
        throw new Error('Image file is required');
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(imageFile.type)) {
        throw new Error('Invalid image format. Only JPG, PNG, GIF, and WebP files are supported.');
      }

      // Validate file size (10MB limit for images)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (imageFile.size > maxSize) {
        throw new Error('Image file size must be less than 10MB');
      }

      // GMB API v4 has CORS restrictions on direct uploads
      // For now, we'll upload to a temporary hosting service and use that URL
      // Alternative: User provides a publicly accessible URL

      logger.log('[uploadMedia] Starting image upload process...');

      // Convert image to base64 for temporary storage/display
      const reader = new FileReader();
      const base64Data = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });

      logger.log('[uploadMedia] Image converted to base64');

      // For scheduled posts, we'll store the base64 data URL
      // For immediate GMB uploads, we need a publicly accessible URL
      // This is a limitation of GMB API v4 - it requires publicly accessible image URLs

      // Since we can't upload directly due to CORS, we'll return the base64 data
      // and let the calling code handle it appropriately
      const result = {
        name: `temp_media_${Date.now()}`,
        mediaFormat: 'PHOTO',
        googleUrl: base64Data,
        sourceUrl: base64Data,
        thumbnailUrl: base64Data,
        description: imageFile.name,
        createTime: new Date().toISOString(),
        locationAssociation: {
          category: category || 'ADDITIONAL'
        }
      };

      logger.log('[uploadMedia] Returning media data with base64 URL');
      return result;
    } catch (error) {
      logger.error('Error uploading media:', error);
      throw error;
    }
  }

  /**
   * Update Business Profile information for a specific location
   * Uses Business Information API v1: locations/{locationId}?updateMask=fields
   * @param {string} locationId - The location ID
   * @param {Object} profileData - The profile data to update
   * @param {string} accessToken - Optional Google OAuth access token
   * @returns {Promise<Object>} Updated location data
   */
  async updateBusinessProfile(locationId, profileData, accessToken) {
    try {
      if (!locationId) throw new Error('locationId is required');
      if (!profileData) throw new Error('profileData is required');

      const locName = String(locationId).includes('locations/')
        ? String(locationId)
        : `locations/${locationId}`;

      // Remove undefined values to avoid invalid payload
      const sanitize = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitize);
        const out = {};
        Object.keys(obj).forEach((k) => {
          const v = obj[k];
          if (v === undefined) return;
          out[k] = sanitize(v);
        });
        return out;
      };
      const cleanedProfileData = sanitize(profileData);

      // Determine which top-level fields are being updated
      const updateFields = Object.keys(cleanedProfileData);

      // IMPORTANT: latlng is required in the body when updating storefrontAddress (for validation)
      // but MUST NOT be included in the updateMask (API returns LAT_LNG_UPDATES_NOT_PERMITTED)
      // This is a quirk of Google's My Business API
      const maskFields = updateFields.filter(field => field !== 'latlng');
      const updateMask = maskFields.join(',');

      const url = `https://mybusinessbusinessinformation.googleapis.com/v1/${locName}?updateMask=${updateMask}`;

      // Log detailed info for service area updates
      if (cleanedProfileData.serviceArea) {
        logger.log('[GMBService] Updating service area with data:', JSON.stringify(cleanedProfileData.serviceArea, null, 2));
        logger.log('[GMBService] Update URL:', url);
        logger.log('[GMBService] Update mask:', updateMask);
      }

      const response = await this._googleFetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanedProfileData)
      }, accessToken);

      if (!response.ok) {
        let errorData = {};
        let rawText = '';
        try { errorData = await response.json(); } catch (_) { try { rawText = await response.text(); } catch (_) { } }
        logger.error('[GMBService] updateBusinessProfile failed', {
          status: response.status,
          statusText: response.statusText,
          url,
          updateMask,
          body: cleanedProfileData,
          error: errorData,
          rawText,
          errorDetails: errorData?.error?.details || null
        });
        logger.error('[GMBService] Full error response:', JSON.stringify(errorData, null, 2));
        const message = errorData?.error?.message || rawText || 'Failed to update business profile';
        throw new Error(message);
      }

      const data = await response.json();

      // Log successful service area update response
      if (cleanedProfileData.serviceArea) {
        logger.log('[GMBService] Service area update successful. Response serviceArea:', JSON.stringify(data?.serviceArea, null, 2));
      }

      return data;
    } catch (error) {
      logger.error('Error updating business profile:', error);
      throw error;
    }
  }

  /**
   * Get Google OAuth token from TokenManager or localStorage
   * If no token exists, returns null (user needs to authenticate)
   * @returns {Promise<string|null>} The access token or null
   */
  async getGoogleToken() {
    try {
      // Try to get token from TokenManager first
      const tokenData = tokenManager.get('google');
      if (tokenData?.access_token) {
        logger.log('🔐 Using existing Google token from TokenManager');
        return tokenData.access_token;
      }

      // Fallback to localStorage
      const storedToken = localStorage.getItem('googleAccessToken');
      if (storedToken) {
        logger.log('🔐 Using existing Google token from localStorage');
        return storedToken;
      }

      // No token available - user needs to authenticate
      logger.warn('⚠️ No Google token available. User needs to authenticate.');
      return null;
    } catch (error) {
      logger.error('Error getting Google token:', error);
      throw error;
    }
  }

  /**
   * Initialize OAuth2 flow with authorization code exchange
   * Alternative to Firebase popup flow for server-side apps
   * @param {string} authorizationCode - Authorization code from OAuth callback
   * @returns {Promise<Object>} Token data
   */
  async exchangeAuthorizationCode(authorizationCode) {
    try {
      const params = new URLSearchParams({
        client_id: this.clientId,
        client_secret: this.clientSecret,
        code: authorizationCode,
        grant_type: 'authorization_code',
        redirect_uri: oauthConfig.google.redirectUri
      });

      const response = await fetch(oauthConfig.google.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error_description || 'Failed to exchange authorization code');
      }

      const tokenData = await response.json();

      // Calculate expiration timestamp
      if (tokenData.expires_in) {
        tokenData.expires_at = Math.floor(Date.now() / 1000) + tokenData.expires_in;
      }
      tokenData.created_at = Date.now();

      // Store tokens
      tokenManager.set('google', tokenData);

      if (tokenData.refresh_token) {
        localStorage.setItem('googleRefreshToken', tokenData.refresh_token);
      }

      logger.log('✅ Authorization code exchanged successfully');
      return tokenData;
    } catch (error) {
      logger.error('Error exchanging authorization code:', error);
      throw error;
    }
  }

  /**
   * Generate OAuth2 authorization URL for manual redirect flow
   * @returns {string} Authorization URL
   */
  generateAuthorizationUrl() {
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: oauthConfig.google.redirectUri,
      scope: oauthConfig.google.scopes.join(' '),
      response_type: 'code',
      access_type: 'offline',
      prompt: 'consent',
      include_granted_scopes: 'true'
    });

    return `${oauthConfig.google.authEndpoint}?${params.toString()}`;
  }

  /**
   * Get GMB accounts for the authenticated user
   * @param {string} accessToken - Google OAuth access token
   * @returns {Promise<Array>} List of GMB accounts
   */
  async getAccounts(accessToken) {
    try {
      // Check if admin is viewing a user's profile
      const adminViewingUserId = this._resolveAdminViewingUserId();
      logger.log(`🔍 [GMBService.getAccounts] URL: ${window.location?.href}, adminViewingUserId: ${adminViewingUserId || 'NULL'}`);
      const queryParams = adminViewingUserId ? `?userId=${encodeURIComponent(adminViewingUserId)}` : '';

      // ALWAYS use backend proxy when admin is viewing a client's profile
      if (adminViewingUserId) {
        logger.log(`🔑 [GMBService] Admin viewing user ${adminViewingUserId}, using backend proxy`);
        const ApiService = (await import('./api')).default;
        const resp = await ApiService.get(`/gmb/accounts${queryParams}`);
        return resp?.data?.accounts || resp?.accounts || [];
      }

      // For normal users, try to get token and call Google API directly
      const token = await this._getAccessToken(accessToken);
      if (!token) {
        const ApiService = (await import('./api')).default;
        const resp = await ApiService.get(`/gmb/accounts${queryParams}`);
        return resp?.data?.accounts || resp?.accounts || [];
      }

      const response = await this._googleFetch(`https://mybusinessaccountmanagement.googleapis.com/v1/accounts`, { method: 'GET' }, token);
      if (response.ok) {
        const data = await response.json();
        return data.accounts || [];
      }
      // On 401 from Google, fallback to backend proxy
      if (response.status === 401) {
        const ApiService = (await import('./api')).default;
        const resp = await ApiService.get(`/gmb/accounts${queryParams}`);
        return resp?.data?.accounts || resp?.accounts || [];
      }
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error?.message || 'Failed to fetch GMB accounts';
      if (response.status === 403) {
        throw new Error(`Permission denied: ${errorMessage}`);
      }
      throw new Error(errorMessage);
    } catch (error) {
      logger.error('Error fetching GMB accounts:', error);
      throw error;
    }
  }

  /**
   * Get locations for a specific GMB account
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountName - The account name (e.g., 'accounts/12345')
   * @returns {Promise<Array>} List of locations
   */
  async getLocations(accessToken, accountName) {
    try {
      if (!accountName) {
        throw new Error('Account name is required to fetch locations');
      }

      // Check if admin is viewing a user's profile
      const adminViewingUserId = this._resolveAdminViewingUserId();
      const queryParams = adminViewingUserId ? `?userId=${encodeURIComponent(adminViewingUserId)}` : '';
      const encodedAccountName = encodeURIComponent(accountName);

      // ALWAYS use backend proxy when admin is viewing a client's profile
      if (adminViewingUserId) {
        logger.log(`🔑 [GMBService] Admin viewing user ${adminViewingUserId}, using backend proxy for locations`);
        const ApiService = (await import('./api')).default;
        const resp = await ApiService.get(`/gmb/locations/${encodedAccountName}${queryParams}`);
        return resp?.data?.locations || resp?.locations || [];
      }

      // For normal users, try to get token and call Google API directly
      const token = await this._getAccessToken(accessToken);
      if (!token) {
        const ApiService = (await import('./api')).default;
        const resp = await ApiService.get(`/gmb/locations/${encodedAccountName}${queryParams}`);
        return resp?.data?.locations || resp?.locations || [];
      }

      const readMask = [
        'storeCode',
        'regularHours',
        'name',
        'languageCode',
        'title',
        'phoneNumbers',
        'categories',
        'storefrontAddress',
        'websiteUri',
        'specialHours',
        'serviceArea',
        'labels',
        'adWordsLocationExtensions',
        'latlng',
        'openInfo',
        'metadata',
        'profile',
        'relationshipData',
        'moreHours'
      ].join(',');

      const url = `https://mybusinessbusinessinformation.googleapis.com/v1/${accountName}/locations?readMask=${encodeURIComponent(readMask)}&pageSize=100`;

      const response = await this._googleFetch(
        url,
        { method: 'GET', timeoutMs: 45000 }, // Increased timeout for large accounts with many locations
        accessToken
      );

      if (!response.ok) {
        // On 401 from Google, fallback to backend proxy
        if (response.status === 401) {
          const ApiService = (await import('./api')).default;
          const resp = await ApiService.get(`/gmb/locations/${encodedAccountName}${queryParams}`);
          return resp?.data?.locations || resp?.locations || [];
        }
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to fetch locations');
      }

      const data = await response.json();
      return data.locations || [];
    } catch (error) {
      logger.error('Error fetching locations:', error);
      throw error;
    }
  }

  /**
   * Reply to a review
   * Supports two call signatures:
   * 1. replyToReview(accessToken, reviewName, comment) - where reviewName is full path
   * 2. replyToReview(accessToken, accountName, locationName, reviewId, comment) - separate parts
   * 
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountNameOrReviewName - Either account name (e.g., 'accounts/12345') OR full review name
   * @param {string} locationNameOrComment - Either location name (e.g., 'locations/67890') OR the reply comment
   * @param {string} reviewIdOrUndefined - (Optional) Review ID if using separate parts
   * @param {string} commentOrUndefined - (Optional) Reply comment if using separate parts
   * @returns {Promise<Object>} Reply response
   */
  async replyToReview(accessToken, accountNameOrReviewName, locationNameOrComment, reviewIdOrUndefined, commentOrUndefined) {
    try {
      let reviewName;
      let comment;

      // Determine which signature is being used
      if (reviewIdOrUndefined !== undefined && commentOrUndefined !== undefined) {
        // Signature 2: replyToReview(accessToken, accountName, locationName, reviewId, comment)
        const accountName = accountNameOrReviewName;
        const locationName = locationNameOrComment;
        const reviewId = reviewIdOrUndefined;
        comment = commentOrUndefined;

        logger.log('[GMBService] Replying to review (5 params):', { accountName, locationName, reviewId });

        // Build the full review name
        const cleanReviewId = reviewId.includes('/reviews/')
          ? reviewId.split('/reviews/').pop()
          : reviewId;
        reviewName = `${accountName}/${locationName}/reviews/${cleanReviewId}`;
      } else {
        // Signature 1: replyToReview(accessToken, reviewName, comment)
        reviewName = accountNameOrReviewName;
        comment = locationNameOrComment;

        logger.log('[GMBService] Replying to review (3 params):', { reviewName });
      }

      const url = `https://mybusiness.googleapis.com/v4/${reviewName}/reply`;

      const response = await this._googleFetch(
        url,
        {
          method: 'PUT',
          body: JSON.stringify({
            comment: comment
          })
        },
        accessToken
      );

      if (!response.ok) {
        const errorData = await response.json();
        logger.error('[GMBService] Error replying to review:', errorData);
        throw new Error(errorData.error?.message || 'Failed to reply to review');
      }

      const data = await response.json();
      logger.log('[GMBService] Successfully replied to review');
      return data;
    } catch (error) {
      logger.error('[GMBService] Error in replyToReview:', error);
      throw error;
    }
  }

  /**
   * Create a Q&A question on a location
   * @param {string} accessToken optional
   * @param {string} locationNameOrId e.g. 'locations/123' or '123'
   * @param {string} text question text
   */
  async createQuestion(accessToken, locationNameOrId, text) {
    if (!locationNameOrId) throw new Error('locationNameOrId is required');
    if (!text || !String(text).trim()) throw new Error('Question text is required');
    const locName = String(locationNameOrId).includes('locations/') ? String(locationNameOrId) : `locations/${locationNameOrId}`;
    const url = `https://mybusinessqanda.googleapis.com/v1/${locName}/questions`;
    const res = await this._googleFetch(url, { method: 'POST', body: JSON.stringify({ text: text.trim() }) }, accessToken);
    if (!res.ok) {
      let err = 'Failed to create question';
      try { const d = await res.json(); err = d?.error?.message || err; } catch (_) { }
      throw new Error(err);
    }
    const data = await res.json();
    return data; // includes name like locations/{id}/questions/{questionId}
  }

  /**
   * Get Q&A questions for a location
   * @param {string} accessToken optional
   * @param {string} locationNameOrId e.g. 'locations/123' or '123'
   * @returns {Promise<Array>} Array of question objects
   */
  async getQuestions(accessToken, locationNameOrId) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      const locName = String(locationNameOrId).includes('locations/') ? String(locationNameOrId) : `locations/${locationNameOrId}`;
      const url = `https://mybusinessqanda.googleapis.com/v1/${locName}/questions`;
      const res = await this._googleFetch(url, { method: 'GET' }, accessToken);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 403 || res.status === 404) {
          logger.warn('[GMBService] Q&A not available for this location');
          return [];
        }
        throw new Error(errorData?.error?.message || 'Failed to fetch questions');
      }
      const data = await res.json();
      return data.questions || [];
    } catch (error) {
      logger.warn('[GMBService] Error fetching questions:', error);
      return [];
    }
  }

  /**
   * Answer a Q&A question
   * @param {string} accessToken optional
   * @param {string} questionNameOrParts either full question resource name or location/question parts
   * @param {string} maybeQuestionId optional question id if parts form used
   * @param {string} answerText answer content
   */
  async answerQuestion(accessToken, questionNameOrParts, maybeQuestionId, answerText) {
    let questionName = '';
    let text = '';
    if (typeof maybeQuestionId === 'string' && typeof answerText === 'string') {
      // Signature: (token, locationName, questionId, answerText)
      const locationName = questionNameOrParts;
      const qid = maybeQuestionId;
      questionName = (String(locationName).includes('locations/')) ? `${locationName}/questions/${qid}` : `locations/${locationName}/questions/${qid}`;
      text = answerText;
    } else {
      // Signature: (token, questionName, answerText)
      questionName = questionNameOrParts;
      text = maybeQuestionId;
    }
    if (!questionName) throw new Error('questionName is required');
    if (!text || !String(text).trim()) throw new Error('Answer text is required');
    const url = `https://mybusinessqanda.googleapis.com/v1/${questionName}/answers`;
    const res = await this._googleFetch(url, { method: 'POST', body: JSON.stringify({ text: text.trim() }) }, accessToken);
    if (!res.ok) {
      let err = 'Failed to post answer';
      try { const d = await res.json(); err = d?.error?.message || err; } catch (_) { }
      throw new Error(err);
    }
    const data = await res.json();
    return data;
  }

  /**
   * Get reviews for a specific location
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountName - The account name (e.g., 'accounts/12345')
   * @param {string} locationName - The location name (e.g., 'locations/67890')
   * @returns {Promise<Array>} List of reviews
   */
  async getReviews(accessToken, accountName, locationName) {
    try {
      let allReviews = [];
      let nextPageToken = null;
      let totalReviewCount = 0;
      let averageRating = 0;

      do {
        const url = `https://mybusiness.googleapis.com/v4/${accountName}/${locationName}/reviews` +
          (nextPageToken ? `?pageToken=${nextPageToken}` : '');

        const response = await this._googleFetch(
          url,
          { method: 'GET' },
          accessToken
        );

        if (!response.ok) {
          const errorData = await response.json();

          // Handle 403 Forbidden errors gracefully - don't throw, just log and return empty result
          if (response.status === 403) {
            logger.warn(`[GMBService] Permission denied for reviews API (${response.status}). This may be due to insufficient permissions.`);
            return {
              reviews: [],
              totalReviewCount: 0,
              averageRating: 0
            };
          }

          throw new Error(errorData.error?.message || 'Failed to fetch reviews');
        }

        const data = await response.json();

        // Add reviews from this page
        if (data.reviews && data.reviews.length > 0) {
          allReviews = allReviews.concat(data.reviews);
        }

        // Store metadata from first response
        if (!nextPageToken) {
          totalReviewCount = data.totalReviewCount || 0;
          averageRating = data.averageRating || 0;
        }

        nextPageToken = data.nextPageToken;

        // Safety check to prevent infinite loops
        if (allReviews.length > 10000) {
          logger.warn('Reached maximum review limit of 10,000');
          break;
        }

      } while (nextPageToken);

      logger.log(`Fetched ${allReviews.length} reviews out of ${totalReviewCount} total`);

      // Return the full response object with all reviews
      return {
        reviews: allReviews,
        totalReviewCount: totalReviewCount,
        averageRating: averageRating,
        nextPageToken: null
      };
    } catch (error) {
      logger.error('Error fetching reviews:', error);
      throw error;
    }
  }

  /**
   * Get performance metrics for a specific location
   * @param {string} accessToken - Google OAuth access token
   * @param {string} locationId - The location ID (e.g., '11157617678660838845')
   * @param {Object} dateRange - Date range object with startDate and endDate
   * @returns {Promise<Object>} Performance metrics data
   */
  async getPerformanceMetrics(accessToken, locationId, dateRange = null) {
    try {
      if (!locationId) {
        throw new Error('Location ID is required');
      }

      // Ensure we have a valid access token with proper scopes
      const token = await this._getAccessToken(accessToken);
      if (!token) {
        throw new Error('No valid access token available. Please re-authenticate with Google My Business.');
      }

      // Default to last 30 days (inclusive) if no date range provided
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 29);
      const defaultDateRange = dateRange || {
        startDate: {
          year: start.getFullYear(),
          month: start.getMonth() + 1,
          day: start.getDate()
        },
        endDate: {
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          day: now.getDate()
        }
      };

      const metrics = [
        'CALL_CLICKS',
        'WEBSITE_CLICKS',
        'BUSINESS_DIRECTION_REQUESTS',
        'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH',
        'BUSINESS_IMPRESSIONS_MOBILE_SEARCH'
      ];

      const params = new URLSearchParams();
      metrics.forEach(metric => params.append('dailyMetrics', metric));
      params.append('dailyRange.startDate.year', defaultDateRange.startDate.year);
      params.append('dailyRange.startDate.month', defaultDateRange.startDate.month);
      params.append('dailyRange.startDate.day', defaultDateRange.startDate.day);
      params.append('dailyRange.endDate.year', defaultDateRange.endDate.year);
      params.append('dailyRange.endDate.month', defaultDateRange.endDate.month);
      params.append('dailyRange.endDate.day', defaultDateRange.endDate.day);

      const url = `https://businessprofileperformance.googleapis.com/v1/locations/${locationId}:fetchMultiDailyMetricsTimeSeries?${params.toString()}`;

      logger.log(`[GMB Service] Fetching performance metrics from: ${url}`);

      const response = await this._googleFetch(
        url,
        { method: 'GET' },
        token
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle specific permission errors
        if (response.status === 403) {
          const errorMessage = errorData.error?.message || 'Permission denied';

          if (errorMessage.includes('caller does not have permission')) {
            throw new Error('Your Google account does not have permission to access Business Profile Performance data. Please ensure:\n' +
              '• You are the owner or manager of this business\n' +
              '• The business has performance data available\n' +
              '• You have re-authenticated with the correct Google account\n' +
              '• The Business Profile Performance API is enabled for your project');
          }

          throw new Error(`Permission denied: ${errorMessage}`);
        }

        if (response.status === 404) {
          throw new Error(`Business location not found. Please verify the location ID: ${locationId}`);
        }

        throw new Error(errorData.error?.message || `API request failed with status ${response.status}`);
      }

      const data = await response.json();
      logger.log(`[GMB Service] Successfully fetched performance data:`, data);

      return this.processPerformanceData(data);
    } catch (error) {
      logger.error('Error fetching performance metrics:', error);
      throw error;
    }
  }

  /**
   * Get search keywords impressions - returns actual search queries users used to find the business
   * Uses GMB Performance API searchkeywords/impressions/monthly endpoint
   * @param {string} accessToken - Google OAuth access token
   * @param {string} locationId - The location ID (e.g., '11157617678660838845')
   * @param {Object} monthlyRange - Optional monthly range object
   * @returns {Promise<Array>} Array of search keywords with impressions
   */
  async getSearchKeywordsImpressions(accessToken, locationId, monthlyRange = null) {
    try {
      if (!locationId) {
        throw new Error('Location ID is required');
      }

      const token = await this._getAccessToken(accessToken);
      if (!token) {
        throw new Error('No valid access token available.');
      }

      // Default to last 3 months if no range provided
      const now = new Date();
      const defaultRange = monthlyRange || {
        startMonth: {
          year: now.getMonth() < 2 ? now.getFullYear() - 1 : now.getFullYear(),
          month: now.getMonth() < 2 ? 12 + now.getMonth() - 1 : now.getMonth() - 1
        },
        endMonth: {
          year: now.getFullYear(),
          month: now.getMonth() + 1
        }
      };

      const params = new URLSearchParams();
      params.append('monthlyRange.startMonth.year', defaultRange.startMonth.year);
      params.append('monthlyRange.startMonth.month', defaultRange.startMonth.month);
      params.append('monthlyRange.endMonth.year', defaultRange.endMonth.year);
      params.append('monthlyRange.endMonth.month', defaultRange.endMonth.month);

      const url = `https://businessprofileperformance.googleapis.com/v1/locations/${locationId}/searchkeywords/impressions/monthly?${params.toString()}`;

      logger.log(`[GMB Service] Fetching search keywords from: ${url}`);

      const response = await this._googleFetch(url, { method: 'GET' }, token);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.warn('[GMB Service] Search keywords API error:', errorData);
        return [];
      }

      const data = await response.json();
      logger.log(`[GMB Service] Search keywords response:`, data);

      // Process the search keywords data
      const keywords = (data.searchKeywordsCounts || []).map(item => ({
        searchKeyword: item.searchKeyword,
        impressions: item.insightsValue?.value || item.insightsValue?.threshold || 0
      }));

      // Sort by impressions descending
      keywords.sort((a, b) => b.impressions - a.impressions);

      return keywords;
    } catch (error) {
      logger.error('Error fetching search keywords impressions:', error);
      return [];
    }
  }
  /**
   * Process raw performance data into a more usable format
   * @param {Object} rawData - Raw API response data
   * @returns {Object} Processed metrics data
   */
  processPerformanceData(rawData) {
    try {
      const processedMetrics = {};

      const accumulateFromTimeSeries = (metricKey, timeSeries) => {
        let totalValue = 0;
        let previousValue = 0;
        let totalAll = 0;
        let daysCount = 0;

        const dated = timeSeries?.datedValues || [];
        const toNum = (v) => {
          const n = parseInt(v, 10);
          return Number.isNaN(n) ? 0 : n;
        };
        totalAll = dated.reduce((sum, item) => sum + toNum(item.value), 0);
        daysCount = dated.length;

        if (dated.length > 15) {
          const mid = Math.floor(dated.length / 2);
          const firstHalf = dated.slice(0, mid).reduce((s, it) => s + toNum(it.value), 0);
          const secondHalf = dated.slice(mid).reduce((s, it) => s + toNum(it.value), 0);
          previousValue = firstHalf;
          totalValue = secondHalf;
        } else {
          totalValue = totalAll;
        }

        let changePercent = 0;
        if (previousValue > 0) {
          changePercent = Math.round(((totalValue - previousValue) / previousValue) * 100);
        } else if (totalValue > 0) {
          changePercent = 100;
        }

        // value: display the TOTAL for the entire selected date range (e.g., full month)
        // change: keep percentage vs previous half-period for context
        processedMetrics[metricKey] = {
          value: totalAll,
          change: changePercent,
          changeText: `${changePercent >= 0 ? '+' : ''}${changePercent}% vs last period`,
          totalAll,
          daysCount
        };
      };

      if (Array.isArray(rawData?.multiDailyMetricTimeSeries)) {
        rawData.multiDailyMetricTimeSeries.forEach((entry) => {
          if (entry?.dailyMetric && entry?.dailyMetricTimeSeries?.timeSeries) {
            accumulateFromTimeSeries(entry.dailyMetric, entry.dailyMetricTimeSeries.timeSeries);
            return;
          }
          if (Array.isArray(entry?.dailyMetricTimeSeries)) {
            entry.dailyMetricTimeSeries.forEach((s) => {
              if (s?.dailyMetric && s?.timeSeries) {
                accumulateFromTimeSeries(s.dailyMetric, s.timeSeries);
              }
            });
          }
        });
      }

      const desktopImpr = processedMetrics.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH?.totalAll || 0;
      const mobileImpr = processedMetrics.BUSINESS_IMPRESSIONS_MOBILE_SEARCH?.totalAll || 0;
      const desktopDays = processedMetrics.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH?.daysCount || 0;
      const mobileDays = processedMetrics.BUSINESS_IMPRESSIONS_MOBILE_SEARCH?.daysCount || 0;
      const totalImpressionsAll = desktopImpr + mobileImpr;
      const daysForAvg = Math.max(desktopDays, mobileDays, 1);
      const avgDailyImpressions = totalImpressionsAll / daysForAvg;
      const estimatedMonthlySearchVolume = Math.round(avgDailyImpressions * 30);

      const websiteClicksVal = processedMetrics.WEBSITE_CLICKS?.value || 0;
      const websiteClicksChange = processedMetrics.WEBSITE_CLICKS?.change || 0;

      const searchVolumeChange = this.calculateAverageChange([
        processedMetrics.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH?.change || 0,
        processedMetrics.BUSINESS_IMPRESSIONS_MOBILE_SEARCH?.change || 0
      ]);

      return {
        localViews: (processedMetrics.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH?.value || 0) + (processedMetrics.BUSINESS_IMPRESSIONS_MOBILE_SEARCH?.value || 0),
        localViewsChange: this.calculateAverageChange([
          processedMetrics.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH?.change || 0,
          processedMetrics.BUSINESS_IMPRESSIONS_MOBILE_SEARCH?.change || 0
        ]),
        callClicks: processedMetrics.CALL_CLICKS?.value || 0,
        callClicksChange: processedMetrics.CALL_CLICKS?.change || 0,
        directionRequests: processedMetrics.BUSINESS_DIRECTION_REQUESTS?.value || 0,
        directionRequestsChange: processedMetrics.BUSINESS_DIRECTION_REQUESTS?.change || 0,
        websiteClicks: websiteClicksVal,
        websiteClicksChange: websiteClicksChange,
        organicClicks: websiteClicksVal,
        organicClicksChange: websiteClicksChange,
        avgSearchVolume: estimatedMonthlySearchVolume,
        avgSearchVolumeChange: searchVolumeChange,
        rawData: processedMetrics
      };
    } catch (error) {
      logger.error('Error processing performance data:', error);
      return {
        localViews: 0,
        localViewsChange: 0,
        callClicks: 0,
        callClicksChange: 0,
        directionRequests: 0,
        directionRequestsChange: 0,
        websiteClicks: 0,
        websiteClicksChange: 0,
        organicClicks: 0,
        organicClicksChange: 0,
        avgSearchVolume: 0,
        avgSearchVolumeChange: 0,
        rawData: {}
      };
    }
  }

  calculateAverageChange(changes) {
    const validChanges = changes.filter(change => !isNaN(change) && change !== null);
    if (validChanges.length === 0) return 0;
    return Math.round(validChanges.reduce((sum, change) => sum + change, 0) / validChanges.length);
  }

  /**
   * Get 12-month trend data for Call Clicks
   * @param {string} accessToken
   * @param {string} locationId
   * @returns {Promise<Object>} Object with currentYear and pastYear arrays (12 values each)
   */
  async getCallClickTrends(accessToken, locationId) {
    try {
      if (!locationId) throw new Error('Location ID is required');

      const token = await this._getAccessToken(accessToken);
      if (!token) throw new Error('No valid access token');

      // We want 12 months of data ending today
      // Actually, to show "Current Year vs Past Year", we ideally need 24 months? 
      // Or "This Year (Jan-Dec)" vs "Last Year (Jan-Dec)"?
      // The Chart shows "Jan, Feb, ...". 
      // Let's fetch the current calendar year (Jan 1 - Now) AND previous calendar year (Jan 1 - Dec 31).

      const now = new Date();
      const currentYear = now.getFullYear();
      const prevYear = currentYear - 1;

      // Fetch Current Year
      const currentStart = { year: currentYear, month: 1, day: 1 };
      const currentEnd = { year: currentYear, month: now.getMonth() + 1, day: now.getDate() };

      // Fetch Previous Year
      const prevStart = { year: prevYear, month: 1, day: 1 };
      const prevEnd = { year: prevYear, month: 12, day: 31 };

      const fetchRange = async (start, end) => {
        const params = new URLSearchParams();
        params.append('dailyMetrics', 'CALL_CLICKS');
        params.append('dailyRange.startDate.year', start.year);
        params.append('dailyRange.startDate.month', start.month);
        params.append('dailyRange.startDate.day', start.day);
        params.append('dailyRange.endDate.year', end.year);
        params.append('dailyRange.endDate.month', end.month);
        params.append('dailyRange.endDate.day', end.day);

        const url = `https://businessprofileperformance.googleapis.com/v1/locations/${locationId}:fetchMultiDailyMetricsTimeSeries?${params.toString()}`;
        const res = await this._googleFetch(url, { method: 'GET' }, token);
        if (!res.ok) return [];
        const data = await res.json();

        // Extract time series
        const timeSeries = data.multiDailyMetricTimeSeries?.[0]?.dailyMetricTimeSeries?.[0]?.timeSeries?.datedValues || [];
        return timeSeries;
      };

      const [currSeries, prevSeries] = await Promise.all([
        fetchRange(currentStart, currentEnd),
        fetchRange(prevStart, prevEnd)
      ]);

      // Aggregate by Month (0-11)
      const aggregateByMonth = (series) => {
        const monthly = new Array(12).fill(0);
        series.forEach(item => {
          const date = item.date; // { year, month, day }
          if (date && date.month >= 1 && date.month <= 12) {
            const val = parseInt(item.value || 0, 10);
            monthly[date.month - 1] += isNaN(val) ? 0 : val;
          }
        });
        return monthly;
      };

      return {
        currentYear: aggregateByMonth(currSeries),
        pastYear: aggregateByMonth(prevSeries)
      };

    } catch (error) {
      logger.error('Error fetching call click trends:', error);
      // Return empty arrays on error
      return { currentYear: new Array(12).fill(0), pastYear: new Array(12).fill(0) };
    }
  }



  /**
   * Get photos for a specific location using the media endpoint
   * Filters media items to return only photos
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountId - Account ID string
   * @param {string} locationId - Location ID string
   * @param {number} maxPhotos - Maximum number of photos to return (default 20)
   * @returns {Promise<Array>} List of photo URLs
   */
  async getPhotos(accessToken, accountId, locationId, maxPhotos = 20) {
    try {
      if (!accountId || !locationId) {
        throw new Error('Invalid accountId or locationId');
      }

      // Get media items
      const mediaItems = await this.getMedia(accessToken, accountId, locationId, 50);

      // Filter for photos only and extract URLs
      const photos = mediaItems
        .filter(item => item.mediaFormat === 'PHOTO' || !item.mediaFormat)
        .slice(0, maxPhotos)
        .map(item => ({
          url: item.googleUrl || item.sourceUrl,
          name: item.name,
          description: item.description,
          category: item.category
        }))
        .filter(photo => photo.url); // Only include items with valid URLs

      logger.log(`[GMBService] Found ${photos.length} photos for location ${locationId}`);
      return photos;
    } catch (error) {
      logger.error('Error fetching photos:', error);
      return [];
    }
  }

  /**
   * Refresh token using client ID and secret
   * @param {string} refreshToken - The refresh token
   * @returns {Promise<Object>} New tokens
   */
  async refreshAccessToken(refreshToken) {
    try {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded'
      };

      // Only include X-Goog-User-Project if project ID is configured
      if (this.projectId && this.projectId !== 'undefined' && this.projectId.trim()) {
        headers['X-Goog-User-Project'] = this.projectId;
      }

      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers,
        body: new URLSearchParams({
          client_id: this.clientId,
          client_secret: this.clientSecret,
          refresh_token: refreshToken,
          grant_type: 'refresh_token'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();

        // If permission denied with project header, try without it
        if (response.status === 403 && headers['X-Goog-User-Project']) {
          logger.warn('Permission denied with X-Goog-User-Project in refresh. Retrying without it...');
          const headersWithoutProject = { ...headers };
          delete headersWithoutProject['X-Goog-User-Project'];

          const retryResponse = await fetch('https://oauth2.googleapis.com/token', {
            method: 'POST',
            headers: headersWithoutProject,
            body: new URLSearchParams({
              client_id: this.clientId,
              client_secret: this.clientSecret,
              refresh_token: refreshToken,
              grant_type: 'refresh_token'
            })
          });

          if (retryResponse.ok) {
            return await retryResponse.json();
          }
        }

        throw new Error(errorData.error_description || 'Failed to refresh token');
      }

      return await response.json();
    } catch (error) {
      logger.error('Error refreshing token:', error);
      throw error;
    }
  }

  /**
   * Get local posts for a specific location
   * Uses GMB v4 localPosts endpoint.
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountId - Account ID string (e.g., '12345')
   * @param {string} locationId - Location ID string (e.g., '67890')
   * @returns {Promise<Array>} List of local posts
   */
  async getLocalPosts(accessToken, accountId, locationId) {
    try {
      if (!accountId || !locationId) {
        throw new Error('Invalid accountId or locationId for local posts');
      }

      logger.log(`[GMBService] Fetching local posts for account: ${accountId}, location: ${locationId}`);

      // Force token refresh before making the API call to ensure we have a valid token
      const freshToken = await tokenManager.getValidAccessToken('google', true);
      const tokenToUse = freshToken || accessToken;

      if (!tokenToUse) {
        logger.error('[GMBService] No valid access token available for local posts');
        throw new Error('Authentication required: No valid access token available');
      }

      const response = await this._googleFetch(
        `https://mybusiness.googleapis.com/v4/accounts/${accountId}/locations/${locationId}/localPosts`,
        { method: 'GET' },
        tokenToUse
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error(`[GMBService] Local posts API error:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });

        if (response.status === 404) {
          logger.warn(`[GMBService] Local posts not found for account ${accountId}, location ${locationId}`);
          return [];
        }

        if (response.status === 401) {
          logger.error('[GMBService] Authentication error (401) when fetching local posts');
          // Clear the token to force re-authentication on next attempt
          tokenManager.clear('google');
          throw new Error('Authentication required: Please sign in to Google again');
        }

        throw new Error(errorData.error?.message || 'Failed to fetch local posts');
      }

      const data = await response.json();
      logger.log(`[GMBService] Local posts response:`, data);

      return Array.isArray(data.localPosts) ? data.localPosts : [];
    } catch (error) {
      logger.error('Error fetching local posts:', error);
      // Don't throw error, return empty array to allow other features to work
      return [];
    }
  }

  /**
   * Update a local post
   * @param {string} accountId - Account ID string (e.g., '12345')
   * @param {string} locationId - Location ID string (e.g., '67890')
   * @param {string} postId - Post ID string
   * @param {Object} postData - Updated post data
   * @param {string} accessToken - Google OAuth access token
   * @returns {Promise<Object>} Updated post data
   */
  async updatePost(accountId, locationId, postId, postData, accessToken) {
    try {
      if (!accountId || !locationId || !postId) {
        throw new Error('Invalid accountId, locationId, or postId for updating post');
      }

      logger.log(`[GMBService] Updating post: ${postId} for account: ${accountId}, location: ${locationId}`);
      logger.log(`[GMBService] Post data:`, postData);

      // Force token refresh before making the API call
      const freshToken = await tokenManager.getValidAccessToken('google', true);
      const tokenToUse = freshToken || accessToken;

      if (!tokenToUse) {
        logger.error('[GMBService] No valid access token available for updating post');
        throw new Error('Authentication required: No valid access token available');
      }

      // Build update mask based on what fields are being updated
      const updateMask = [];
      if (postData.summary) updateMask.push('summary');
      if (postData.callToAction) updateMask.push('callToAction');
      if (postData.event) {
        updateMask.push('event');
        if (postData.event.schedule) updateMask.push('event.schedule');
      }
      if (postData.offer) updateMask.push('offer');

      const url = `https://mybusiness.googleapis.com/v4/accounts/${accountId}/locations/${locationId}/localPosts/${postId}?updateMask=${updateMask.join(',')}`;

      const response = await this._googleFetch(
        url,
        {
          method: 'PATCH',
          body: JSON.stringify(postData)
        },
        tokenToUse
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Error updating post:', errorData.error?.message || 'Unknown error');
        throw new Error(errorData.error?.message || 'Failed to update post');
      }

      const data = await response.json();
      logger.log('[GMBService] Post updated successfully:', data);

      return data;
    } catch (error) {
      logger.error('Error updating post:', error);
      throw error;
    }
  }

  /**
   * Delete a local post
   * @param {string} accountId - Account ID string (e.g., '12345')
   * @param {string} locationId - Location ID string (e.g., '67890')
   * @param {string} postId - Post ID string
   * @param {string} accessToken - Google OAuth access token
   * @returns {Promise<void>}
   */
  async deletePost(accountId, locationId, postId, accessToken) {
    try {
      if (!accountId || !locationId || !postId) {
        throw new Error('Invalid accountId, locationId, or postId for deleting post');
      }

      logger.log(`[GMBService] Deleting post: ${postId} for account: ${accountId}, location: ${locationId}`);

      // Force token refresh before making the API call
      const freshToken = await tokenManager.getValidAccessToken('google', true);
      const tokenToUse = freshToken || accessToken;

      if (!tokenToUse) {
        logger.error('[GMBService] No valid access token available for deleting post');
        throw new Error('Authentication required: No valid access token available');
      }

      const url = `https://mybusiness.googleapis.com/v4/accounts/${accountId}/locations/${locationId}/localPosts/${postId}`;

      const response = await this._googleFetch(
        url,
        {
          method: 'DELETE'
        },
        tokenToUse
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Error deleting post:', errorData.error?.message || 'Unknown error');
        throw new Error(errorData.error?.message || 'Failed to delete post');
      }

      logger.log('[GMBService] Post deleted successfully');

      return;
    } catch (error) {
      logger.error('Error deleting post:', error);
      throw error;
    }
  }

  /**
   * Create a local post for a specific location
   * Uses GMB v4 localPosts endpoint.
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountId - Account ID string (e.g., '12345')
   * @param {string} locationId - Location ID string (e.g., '67890')
   * @param {Object} postData - The post data to create
   * @returns {Promise<Object>} Created local post
   */
  async createLocalPost(accessToken, accountId, locationId, postData) {
    try {
      if (!accountId || !locationId) {
        throw new Error('Invalid accountId or locationId for creating local post');
      }

      const cleanAccountId = String(accountId).replace('accounts/', '');
      const cleanLocationId = String(locationId).replace('locations/', '');

      logger.log(`[GMBService] Creating local post for account: ${cleanAccountId}, location: ${cleanLocationId}`);
      logger.log(`[GMBService] Post data:`, postData);

      const response = await this._googleFetch(
        `https://mybusiness.googleapis.com/v4/accounts/${cleanAccountId}/locations/${cleanLocationId}/localPosts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(postData)
        },
        accessToken
      );

      if (!response.ok) {
        // Try to read detailed error body (JSON or text)
        let errorData = {};
        let rawText = '';
        try {
          errorData = await response.json();
        } catch (_) {
          try { rawText = await response.text(); } catch (_) { }
        }
        logger.error(`[GMBService] Create local post API error:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
          rawText
        });
        const message = errorData?.error?.message || rawText || 'Failed to create local post';
        throw new Error(message);
      }

      const data = await response.json();
      logger.log(`[GMBService] Create local post response:`, data);

      return data;
    } catch (error) {
      logger.error('Error creating local post:', error);
      throw error;
    }
  }

  /**
   * Update a local post for a specific location
   * Uses GMB v4 localPosts/{postId}?updateMask=... endpoint.
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountId - Account ID string (e.g., '12345')
   * @param {string} locationId - Location ID string (e.g., '67890')
   * @param {string} postId - Local post ID (numeric string)
   * @param {Object} updateData - The fields to update, e.g., { summary, event }
   * @param {string|string[]} updateMask - Comma-separated fields or array
   * @returns {Promise<Object>} Updated local post
   */
  async updateLocalPost(accessToken, accountId, locationId, postId, updateData, updateMask) {
    try {
      if (!accountId || !locationId || !postId) {
        throw new Error('accountId, locationId and postId are required for updating local post');
      }

      const mask = Array.isArray(updateMask) ? updateMask.join(',') : (updateMask || 'summary,event');

      const url = `https://mybusiness.googleapis.com/v4/accounts/${accountId}/locations/${locationId}/localPosts/${postId}?updateMask=${encodeURIComponent(mask)}`;

      const response = await this._googleFetch(
        url,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData || {})
        },
        accessToken
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('[GMBService] Update local post API error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error?.message || 'Failed to update local post');
      }

      return await response.json();
    } catch (error) {
      logger.error('Error updating local post:', error);
      throw error;
    }
  }

  /**
   * Create a scheduled local post for a specific location
   * Uses GMB v4 localPosts endpoint with scheduledTime field.
   * @param {string} accessToken - Google OAuth access token
   * @param {string} accountId - Account ID string (e.g., '12345')
   * @param {string} locationId - Location ID string (e.g., '67890')
   * @param {Object} postData - The post data including scheduledTime
   * @returns {Promise<Object>} Created scheduled local post
   */
  async createScheduledPost(accessToken, accountId, locationId, postData) {
    try {
      if (!accountId || !locationId) {
        throw new Error('Invalid accountId or locationId for creating scheduled post');
      }

      if (!postData.scheduledTime) {
        throw new Error('scheduledTime is required for scheduled posts');
      }

      logger.log(`[GMBService] Creating scheduled post for account: ${accountId}, location: ${locationId}`);
      logger.log(`[GMBService] Scheduled post data:`, postData);

      const response = await this._googleFetch(
        `https://mybusiness.googleapis.com/v4/accounts/${accountId}/locations/${locationId}/localPosts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(postData)
        },
        accessToken
      );

      if (!response.ok) {
        let errorData = {};
        let rawText = '';
        try {
          errorData = await response.json();
        } catch (_) {
          try { rawText = await response.text(); } catch (_) { }
        }
        logger.error(`[GMBService] Create scheduled post API error:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
          rawText
        });
        const message = errorData?.error?.message || rawText || 'Failed to create scheduled post';
        throw new Error(message);
      }

      const data = await response.json();
      logger.log(`[GMBService] Create scheduled post response:`, data);

      return data;
    } catch (error) {
      logger.error('Error creating scheduled post:', error);
      throw error;
    }
  }

  /**
   * Get products for a specific location
   * Uses My Business API v4: GET /v4/accounts/{accountId}/locations/{locationId}/products
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {string} accessToken - optional Google OAuth access token
   * @param {string} accountId - optional account ID, will be fetched if not provided
   * @returns {Promise<Array>} Array of product objects
   */
  async getProducts(locationNameOrId, accessToken, accountId = null) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');

      // Extract location ID from full name if needed
      const locationId = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId).split('/')[1]
        : String(locationNameOrId);

      // Get account ID if not provided
      if (!accountId) {
        const accounts = await this.getAccounts(accessToken);
        if (accounts.length === 0) throw new Error('No accounts found');
        accountId = accounts[0].name.split('/')[1]; // Extract ID from 'accounts/123456'
      }

      const url = `https://mybusiness.googleapis.com/v4/accounts/${accountId}/locations/${locationId}/products`;

      logger.log(`[GMBService] Fetching products from URL: ${url}`);
      logger.log(`[GMBService] Using account: ${accountId}, location: ${locationId}`);
      logger.log(`[GMBService] Access token available: ${!!accessToken}`);

      const response = await this._googleFetch(
        url,
        { method: 'GET' },
        accessToken
      );

      logger.log(`[GMBService] Products API response status: ${response.status}`);
      logger.log(`[GMBService] Products API response headers:`, Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error(`[GMBService] Products API error:`, {
          status: response.status,
          statusText: response.statusText,
          url: url,
          error: errorData,
          errorMessage: errorData.error?.message || 'Unknown error'
        });
        return [];
      }

      const data = await response.json();
      logger.log(`[GMBService] Products API response data:`, data);
      logger.log(`[GMBService] Products found: ${data.products ? data.products.length : 0}`);
      return data.products || [];
    } catch (error) {
      logger.error('[GMBService] Error fetching products:', error);
      logger.error('[GMBService] Error details:', {
        message: error.message,
        stack: error.stack,
        locationId: locationNameOrId
      });

      // Return mock data when API fails due to CORS
      if (error.message === 'Failed to fetch') {
        logger.warn('[GMBService] Returning mock products data due to CORS limitations');
        return this._getMockProducts();
      }
      return [];
    }
  }

  // DEPRECATED: getServices extracted from categories (replaced by getServiceItems API)
  async getServices() {
    logger.warn('[GMBService] getServices is deprecated. Use getServiceItems(locationNameOrId) instead.');
    return [];
  }

  _normalizeLanguageCode(value) {
    if (!value) return 'en-US';
    return value.includes('-') ? value : `${value}-US`;
  }

  _looksLikeStructuredServiceTypeId(value) {
    if (!value || typeof value !== 'string') return false;
    return value.includes(':') || value.includes('/');
  }

  _buildServiceItemPayload(service, overrides = {}) {
    const structured = service.rawServiceItem?.structuredServiceItem;
    const freeForm = service.rawServiceItem?.freeFormServiceItem;
    const labelInfo = freeForm?.label;

    if (service.freeForm || freeForm) {
      const freeFormCategory = overrides.category ?? freeForm?.category ?? service.category;
      const labelOverrides = overrides.label || {};
      const label = {
        displayName: labelOverrides.displayName ?? labelInfo?.displayName ?? service.name ?? '',
        description: labelOverrides.description ?? labelInfo?.description ?? service.description ?? '',
        languageCode: this._normalizeLanguageCode(
          labelOverrides.languageCode ?? labelInfo?.languageCode ?? service.languageCode
        )
      };
      const freeFormPayload = { label };
      if (freeFormCategory) {
        freeFormPayload.category = freeFormCategory;
      }

      return {
        freeFormServiceItem: freeFormPayload
      };
    }

    return {
      structuredServiceItem: {
        serviceTypeId: overrides.serviceTypeId ?? structured?.serviceTypeId ?? service.serviceTypeId ?? service.id,
        description: overrides.description ?? structured?.description ?? service.description ?? ''
      }
    };
  }

  /**
   * Update service items for a specific location
   * Uses Business Information API v1 with PATCH method
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {Array} serviceItems - Array of service items to update
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Updated location data with service items
   */
  async updateServiceItems(locationNameOrId, serviceItems, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      if (!serviceItems || !Array.isArray(serviceItems)) throw new Error('serviceItems must be an array');

      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      const url = `https://mybusinessbusinessinformation.googleapis.com/v1/${locName}?updateMask=serviceItems`;

      logger.log(`[GMBService] Updating service items for: ${locName}`);
      logger.log(`[GMBService] Service items to update:`, serviceItems);

      const response = await this._googleFetch(
        url,
        {
          method: 'PATCH',
          body: JSON.stringify({
            serviceItems: serviceItems
          })
        },
        accessToken
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Error updating service items:', errorData.error?.message || 'Unknown error');
        throw new Error(errorData.error?.message || 'Failed to update service items');
      }

      const data = await response.json();
      logger.log('[GMBService] Service items updated successfully:', data);

      return data;
    } catch (error) {
      logger.error('Error updating service items:', error);
      throw error;
    }
  }

  /**
   * Add a new service item to a location
   * This appends a new service to the existing serviceItems array
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {Object} newService - New service item to add
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Updated location data with service items
   */
  async addServiceItem(locationNameOrId, newService, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      if (!newService) throw new Error('newService is required');

      // First, get existing service items
      const existingServices = await this.getServiceItems(locationNameOrId, accessToken);

      logger.log(`[GMBService] Existing services:`, existingServices);
      logger.log(`[GMBService] Adding new service:`, newService);

      // Transform existing services back to API format
      const existingServiceItems = existingServices.map(service => this._buildServiceItemPayload(service));

      // Add the new service (free-form unless a structured serviceTypeId is recognized)
      const shouldUseStructured = this._looksLikeStructuredServiceTypeId(newService.serviceTypeId);
      if (!shouldUseStructured && !newService.category) {
        throw new Error('category is required to create a free-form service item');
      }
      const newServiceItem = shouldUseStructured
        ? {
          structuredServiceItem: {
            serviceTypeId: newService.serviceTypeId,
            description: newService.description || ''
          }
        }
        : {
          freeFormServiceItem: {
            ...(newService.category ? { category: newService.category } : {}),
            label: {
              displayName: newService.name,
              description: newService.description || '',
              languageCode: 'en-US'
            }
          }
        };

      const updatedServiceItems = [...existingServiceItems];
      if (shouldUseStructured) {
        const duplicateIndex = updatedServiceItems.findIndex(item =>
          item.structuredServiceItem?.serviceTypeId === newService.serviceTypeId
        );
        if (duplicateIndex >= 0) {
          updatedServiceItems[duplicateIndex] = newServiceItem;
        } else {
          updatedServiceItems.push(newServiceItem);
        }
      } else {
        updatedServiceItems.push(newServiceItem);
      }

      // Update the location with all service items
      return await this.updateServiceItems(locationNameOrId, updatedServiceItems, accessToken);
    } catch (error) {
      logger.error('Error adding service item:', error);
      throw error;
    }
  }

  /**
   * Update a specific service item in a location
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {string} serviceId - ID of the service to update
   * @param {Object} updatedService - Updated service data
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Updated location data with service items
   */
  async updateServiceItem(locationNameOrId, serviceId, updatedService, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      if (!serviceId) throw new Error('serviceId is required');
      if (!updatedService) throw new Error('updatedService is required');

      // First, get existing service items
      const existingServices = await this.getServiceItems(locationNameOrId, accessToken);

      logger.log(`[GMBService] Existing services:`, existingServices);
      logger.log(`[GMBService] Updating service with ID:`, serviceId);
      logger.log(`[GMBService] Updated service data:`, updatedService);

      // Transform existing services back to API format, updating the target service
      const updatedServiceItems = existingServices.map(service => {
        const isTarget = service.id === serviceId || service.serviceTypeId === serviceId;
        if (!isTarget) {
          return this._buildServiceItemPayload(service);
        }

        const overrides = {
          description: updatedService.description ?? service.description
        };

        if (service.freeForm) {
          const categoryValue = updatedService.category ?? service.category;
          overrides.label = {
            displayName: updatedService.name ?? service.name,
            description: updatedService.description ?? service.description,
            languageCode: updatedService.languageCode ?? service.languageCode
          };
          if (categoryValue) {
            overrides.category = categoryValue;
          }
        } else {
          overrides.serviceTypeId = updatedService.serviceTypeId || service.serviceTypeId || service.id;
        }

        return this._buildServiceItemPayload(service, overrides);
      });

      // Update the location with all service items
      return await this.updateServiceItems(locationNameOrId, updatedServiceItems, accessToken);
    } catch (error) {
      logger.error('Error updating service item:', error);
      throw error;
    }
  }

  /**
   * Delete a specific service item from a location
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {string} serviceId - ID of the service to delete
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Updated location data with service items
   */
  async deleteServiceItem(locationNameOrId, serviceId, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      if (!serviceId) throw new Error('serviceId is required');

      // First, get existing service items
      const existingServices = await this.getServiceItems(locationNameOrId, accessToken);

      logger.log(`[GMBService] Existing services:`, existingServices);
      logger.log(`[GMBService] Deleting service with ID:`, serviceId);

      // Filter out the service to delete
      const filteredServices = existingServices.filter(
        service => service.id !== serviceId && service.serviceTypeId !== serviceId
      );

      // Transform remaining services back to API format
      const updatedServiceItems = filteredServices.map(service => this._buildServiceItemPayload(service));

      // Update the location with remaining service items
      return await this.updateServiceItems(locationNameOrId, updatedServiceItems, accessToken);
    } catch (error) {
      logger.error('Error deleting service item:', error);
      throw error;
    }
  }

  // ==================== PLACE ACTION LINKS (APPOINTMENT LINKS) ====================

  /**
   * Add a place action link (appointment booking link) to a location
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {Object} linkData - Link data object
   * @param {string} linkData.placeActionType - Type of action (APPOINTMENT, ONLINE_APPOINTMENT, etc.)
   * @param {string} linkData.uri - The URL for the action
   * @param {string} linkData.label - Optional label for the link
   * @param {string} linkData.description - Optional description
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Updated location data
   */
  async addPlaceActionLink(locationNameOrId, linkData, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      if (!linkData || !linkData.uri) throw new Error('linkData with uri is required');

      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      // Get current location data to retrieve existing place action links
      const locationData = await this.getLocation(locationNameOrId, accessToken);
      const existingLinks = locationData.placeActionLinks || [];

      // Add the new link
      const newLink = {
        placeActionType: linkData.placeActionType || 'APPOINTMENT',
        uri: linkData.uri,
        ...(linkData.label && { label: linkData.label }),
        ...(linkData.description && { description: linkData.description })
      };

      const updatedLinks = [...existingLinks, newLink];

      const url = `https://mybusinessbusinessinformation.googleapis.com/v1/${locName}?updateMask=placeActionLinks`;

      logger.log(`[GMBService] Adding place action link to: ${locName}`);
      logger.log(`[GMBService] New link:`, newLink);

      const response = await this._googleFetch(
        url,
        {
          method: 'PATCH',
          body: JSON.stringify({
            placeActionLinks: updatedLinks
          })
        },
        accessToken
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Error adding place action link:', errorData.error?.message || 'Unknown error');
        throw new Error(errorData.error?.message || 'Failed to add place action link');
      }

      const data = await response.json();
      logger.log('[GMBService] Place action link added successfully:', data);

      return data;
    } catch (error) {
      logger.error('Error adding place action link:', error);
      throw error;
    }
  }

  /**
   * Update a place action link
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {string} originalUri - The original URI to identify which link to update
   * @param {Object} linkData - Updated link data
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Updated location data
   */
  async updatePlaceActionLink(locationNameOrId, originalUri, linkData, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      if (!originalUri) throw new Error('originalUri is required');
      if (!linkData || !linkData.uri) throw new Error('linkData with uri is required');

      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      // Get current location data to retrieve existing place action links
      const locationData = await this.getLocation(locationNameOrId, accessToken);
      const existingLinks = locationData.placeActionLinks || [];

      // Find and update the link
      const updatedLinks = existingLinks.map(link => {
        if (link.uri === originalUri) {
          return {
            placeActionType: linkData.placeActionType || link.placeActionType || 'APPOINTMENT',
            uri: linkData.uri,
            ...(linkData.label && { label: linkData.label }),
            ...(linkData.description && { description: linkData.description })
          };
        }
        return link;
      });

      const url = `https://mybusinessbusinessinformation.googleapis.com/v1/${locName}?updateMask=placeActionLinks`;

      logger.log(`[GMBService] Updating place action link in: ${locName}`);
      logger.log(`[GMBService] Original URI:`, originalUri);
      logger.log(`[GMBService] Updated link data:`, linkData);

      const response = await this._googleFetch(
        url,
        {
          method: 'PATCH',
          body: JSON.stringify({
            placeActionLinks: updatedLinks
          })
        },
        accessToken
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Error updating place action link:', errorData.error?.message || 'Unknown error');
        throw new Error(errorData.error?.message || 'Failed to update place action link');
      }

      const data = await response.json();
      logger.log('[GMBService] Place action link updated successfully:', data);

      return data;
    } catch (error) {
      logger.error('Error updating place action link:', error);
      throw error;
    }
  }

  /**
   * Delete a place action link
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {string} uri - The URI of the link to delete
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Updated location data
   */
  async deletePlaceActionLink(locationNameOrId, uri, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      if (!uri) throw new Error('uri is required');

      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      // Get current location data to retrieve existing place action links
      const locationData = await this.getLocation(locationNameOrId, accessToken);
      const existingLinks = locationData.placeActionLinks || [];

      // Filter out the link to delete
      const updatedLinks = existingLinks.filter(link => link.uri !== uri);

      const url = `https://mybusinessbusinessinformation.googleapis.com/v1/${locName}?updateMask=placeActionLinks`;

      logger.log(`[GMBService] Deleting place action link from: ${locName}`);
      logger.log(`[GMBService] URI to delete:`, uri);

      const response = await this._googleFetch(
        url,
        {
          method: 'PATCH',
          body: JSON.stringify({
            placeActionLinks: updatedLinks
          })
        },
        accessToken
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Error deleting place action link:', errorData.error?.message || 'Unknown error');
        throw new Error(errorData.error?.message || 'Failed to delete place action link');
      }

      const data = await response.json();
      logger.log('[GMBService] Place action link deleted successfully:', data);

      return data;
    } catch (error) {
      logger.error('Error deleting place action link:', error);
      throw error;
    }
  }

  /**
   * Get mock products data when API fails due to CORS
   * @returns {Array} Array of mock product objects
   * @private
   */
  _getMockProducts() {
    return [
      {
        name: 'locations/8588992893892271088/products/product1',
        productId: 'product1',
        title: 'Premium Web Design',
        description: 'Custom responsive website design with modern UI/UX',
        price: {
          currencyCode: 'USD',
          units: '999',
          nanos: 0
        },
        category: 'Web Development',
        labels: ['Featured', 'Popular']
      },
      {
        name: 'locations/8588992893892271088/products/product2',
        productId: 'product2',
        title: 'SEO Optimization Package',
        description: 'Complete SEO audit and optimization for better search rankings',
        price: {
          currencyCode: 'USD',
          units: '499',
          nanos: 0
        },
        category: 'Digital Marketing',
        labels: ['Best Value']
      },
      {
        name: 'locations/8588992893892271088/products/product3',
        productId: 'product3',
        title: 'Mobile App Development',
        description: 'Native iOS and Android app development',
        price: {
          currencyCode: 'USD',
          units: '2999',
          nanos: 0
        },
        category: 'Mobile Development',
        labels: ['Enterprise']
      }
    ];
  }

  /**
   * Get mock services data when API fails due to CORS
   * @returns {Array} Array of mock service objects
   * @private
   */
  _getMockServices() {
    return [
      {
        name: 'locations/8588992893892271088/services/service1',
        serviceId: 'service1',
        displayName: 'Website Consultation',
        description: 'Free 30-minute consultation to discuss your website needs',
        category: 'Consultation',
        isOffered: true
      },
      {
        name: 'locations/8588992893892271088/services/service2',
        serviceId: 'service2',
        displayName: 'Technical Support',
        description: '24/7 technical support for all our web solutions',
        category: 'Support',
        isOffered: true
      },
      {
        name: 'locations/8588992893892271088/services/service3',
        serviceId: 'service3',
        displayName: 'Website Maintenance',
        description: 'Monthly website updates, security patches, and backups',
        category: 'Maintenance',
        isOffered: true
      },
      {
        name: 'locations/8588992893892271088/services/service4',
        serviceId: 'service4',
        displayName: 'Domain & Hosting Setup',
        description: 'Complete domain registration and hosting configuration',
        category: 'Infrastructure',
        isOffered: true
      }
    ];
  }

  /**
   * Get a single location by ID with full metadata (including newReviewUri)
   * Uses Business Information API v1: locations/{locationId}?readMask=...
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Location data with metadata
   */
  async getLocation(locationNameOrId, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');

      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      // Full readMask to get all fields including metadata with newReviewUri
      const readMask = [
        'storeCode',
        'regularHours',
        'name',
        'languageCode',
        'title',
        'phoneNumbers',
        'categories',
        'storefrontAddress',
        'websiteUri',
        'specialHours',
        'serviceArea',
        'labels',
        'adWordsLocationExtensions',
        'latlng',
        'openInfo',
        'metadata',
        'profile',
        'relationshipData',
        'moreHours',
        'serviceItems'
      ].join(',');

      const url = `https://mybusinessbusinessinformation.googleapis.com/v1/${locName}?readMask=${encodeURIComponent(readMask)}`;

      logger.log(`[GMBService] Fetching single location: ${locName}`);

      const response = await this._googleFetch(url, { method: 'GET' }, accessToken);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('[GMBService] getLocation error:', errorData.error?.message || 'Unknown error');
        throw new Error(errorData.error?.message || 'Failed to fetch location');
      }

      const data = await response.json();
      logger.log('[GMBService] Location fetched successfully:', {
        name: data.name,
        title: data.title,
        hasMetadata: !!data.metadata,
        hasNewReviewUri: !!data.metadata?.newReviewUri,
        newReviewUri: data.metadata?.newReviewUri
      });

      return data;
    } catch (error) {
      logger.error('Error fetching location:', error);
      throw error;
    }
  }

  /**
   * Get Voice of Merchant verification state for a Business Profile location
   * API: https://mybusinessverifications.googleapis.com/v1/locations/{locationId}/VoiceOfMerchantState
   * @param {string} locationNameOrId - e.g., 'locations/11157617678660838845' or just the numeric ID
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<{ raw: object, simplifiedStatus: 'verified'|'suspended'|'pending_verification'|'not_verified'|'unknown' }>}
   */
  async getVoiceOfMerchantState(locationNameOrId, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      const url = `https://mybusinessverifications.googleapis.com/v1/${locName}/VoiceOfMerchantState`;
      logger.log('[GMBService] Fetching VoiceOfMerchantState:', url);
      const response = await this._googleFetch(url, { method: 'GET' }, accessToken);
      if (!response.ok) {
        const rawText = await response.text().catch(() => '');
        logger.error('[GMBService] VoiceOfMerchantState error', {
          url,
          status: response.status,
          statusText: response.statusText,
          body: rawText
        });
        let errorMessage = 'Failed to fetch VoiceOfMerchantState';
        try {
          const errorData = JSON.parse(rawText);
          errorMessage = errorData?.error?.message || errorMessage;
        } catch (_) { }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Derive a simplified status from whatever fields are present
      const rawState = (data?.state || data?.status || data?.voiceOfMerchantState || '').toString().toUpperCase();
      let simplified = 'unknown';

      if (rawState.includes('VERIFIED')) simplified = 'verified';
      else if (rawState.includes('SUSPENDED')) simplified = 'suspended';
      else if (rawState.includes('PENDING')) simplified = 'pending_verification';
      else if (data?.isVerified === true) simplified = 'verified';
      else if (data?.isVerified === false) simplified = 'not_verified';
      else simplified = 'not_verified';

      return { raw: data, simplifiedStatus: simplified };
    } catch (error) {
      logger.error('Error fetching VoiceOfMerchantState:', error);
      throw error;
    }
  }

  /**
   * Fetch available verification options for a location
   * API: https://mybusinessverifications.googleapis.com/v1/locations/{locationId}:fetchVerificationOptions
   * @param {string} locationNameOrId - e.g., 'locations/11157617678660838845' or just the numeric ID
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Available verification options
   */
  async fetchVerificationOptions(locationNameOrId, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      const url = `https://mybusinessverifications.googleapis.com/v1/${locName}:fetchVerificationOptions`;
      logger.log('[GMBService] Fetching verification options:', url);

      const response = await this._googleFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          languageCode: 'en'
        })
      }, accessToken);

      if (!response.ok) {
        const rawText = await response.text().catch(() => '');
        logger.error('[GMBService] fetchVerificationOptions error', {
          url,
          status: response.status,
          statusText: response.statusText,
          body: rawText
        });
        let errorMessage = 'Failed to fetch verification options';
        let errorData = {};
        try {
          errorData = JSON.parse(rawText);
          errorMessage = errorData?.error?.message || errorMessage;
        } catch (_) { }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      logger.log('[GMBService] Verification options:', data);

      // Parse options into a user-friendly format
      const options = (data.options || []).map(opt => ({
        method: opt.verificationMethod || 'UNKNOWN',
        // Phone verification details
        phoneNumber: opt.phoneData?.phoneNumber || null,
        // Address verification details (postcard)
        addressData: opt.addressData || null,
        // Email verification details
        emailData: opt.emailData || null
      }));

      return {
        success: true,
        options,
        raw: data
      };
    } catch (error) {
      logger.error('Error fetching verification options:', error);
      return {
        success: false,
        error: error.message,
        options: []
      };
    }
  }

  /**
   * Request verification for a location (initiate the verification process)
   * API: https://mybusinessverifications.googleapis.com/v1/locations/{locationId}:verify
   * @param {string} locationNameOrId - e.g., 'locations/11157617678660838845' or just the numeric ID
   * @param {string} method - Verification method: 'ADDRESS', 'PHONE_CALL', 'SMS', 'EMAIL', 'AUTO'
   * @param {Object} verificationData - Additional data for the verification (e.g., phone number, email)
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Verification request result
   */
  async requestVerification(locationNameOrId, method, verificationData = {}, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      if (!method) throw new Error('Verification method is required');

      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      const url = `https://mybusinessverifications.googleapis.com/v1/${locName}:verify`;
      logger.log('[GMBService] Requesting verification:', { url, method });

      // Build request body based on method
      const requestBody = {
        method: method.toUpperCase(),
        languageCode: 'en'
      };

      // Add method-specific data
      if (method.toUpperCase() === 'PHONE_CALL' || method.toUpperCase() === 'SMS') {
        if (verificationData.phoneNumber) {
          requestBody.phoneInput = {
            phoneNumber: verificationData.phoneNumber
          };
        }
      } else if (method.toUpperCase() === 'EMAIL') {
        if (verificationData.emailAddress) {
          requestBody.emailInput = {
            emailAddress: verificationData.emailAddress
          };
        }
      } else if (method.toUpperCase() === 'ADDRESS') {
        if (verificationData.mailerContactName) {
          requestBody.mailerContactName = verificationData.mailerContactName;
        }
      }

      const response = await this._googleFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      }, accessToken);

      if (!response.ok) {
        const rawText = await response.text().catch(() => '');
        logger.error('[GMBService] requestVerification error', {
          url,
          status: response.status,
          statusText: response.statusText,
          body: rawText
        });
        let errorMessage = 'Failed to request verification';
        let errorData = {};
        try {
          errorData = JSON.parse(rawText);
          errorMessage = errorData?.error?.message || errorMessage;
        } catch (_) { }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      logger.log('[GMBService] Verification requested:', data);

      return {
        success: true,
        verification: data.verification || data,
        message: 'Verification request sent successfully'
      };
    } catch (error) {
      logger.error('Error requesting verification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Complete verification by submitting the PIN code
   * API: https://mybusinessverifications.googleapis.com/v1/locations/{locationId}/verifications/{verificationId}:complete
   * @param {string} locationNameOrId - e.g., 'locations/11157617678660838845' or just the numeric ID
   * @param {string} verificationId - The verification ID from the verification request
   * @param {string} pin - The PIN code received by the user
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} Verification completion result
   */
  async completeVerification(locationNameOrId, verificationId, pin, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      if (!verificationId) throw new Error('verificationId is required');
      if (!pin) throw new Error('PIN code is required');

      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      const url = `https://mybusinessverifications.googleapis.com/v1/${locName}/verifications/${verificationId}:complete`;
      logger.log('[GMBService] Completing verification:', { url, verificationId });

      const response = await this._googleFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      }, accessToken);

      if (!response.ok) {
        const rawText = await response.text().catch(() => '');
        logger.error('[GMBService] completeVerification error', {
          url,
          status: response.status,
          statusText: response.statusText,
          body: rawText
        });
        let errorMessage = 'Failed to complete verification';
        let errorData = {};
        try {
          errorData = JSON.parse(rawText);
          errorMessage = errorData?.error?.message || errorMessage;
        } catch (_) { }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      logger.log('[GMBService] Verification completed:', data);

      return {
        success: true,
        verification: data.verification || data,
        message: 'Verification completed successfully! Your business is now verified.'
      };
    } catch (error) {
      logger.error('Error completing verification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * List all verifications for a location
   * API: https://mybusinessverifications.googleapis.com/v1/locations/{locationId}/verifications
   * @param {string} locationNameOrId - e.g., 'locations/11157617678660838845' or just the numeric ID
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Object>} List of verifications
   */
  async listVerifications(locationNameOrId, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');
      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      const url = `https://mybusinessverifications.googleapis.com/v1/${locName}/verifications`;
      logger.log('[GMBService] Listing verifications:', url);

      const response = await this._googleFetch(url, { method: 'GET' }, accessToken);

      if (!response.ok) {
        const rawText = await response.text().catch(() => '');
        logger.error('[GMBService] listVerifications error', {
          url,
          status: response.status,
          statusText: response.statusText,
          body: rawText
        });
        let errorMessage = 'Failed to list verifications';
        try {
          const errorData = JSON.parse(rawText);
          errorMessage = errorData?.error?.message || errorMessage;
        } catch (_) { }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      logger.log('[GMBService] Verifications list:', data);

      // Find the most recent pending verification
      const verifications = data.verifications || [];
      const pendingVerification = verifications.find(v =>
        v.state === 'PENDING' || v.state === 'VERIFICATION_REQUESTED'
      );

      return {
        success: true,
        verifications,
        pendingVerification,
        raw: data
      };
    } catch (error) {
      logger.error('Error listing verifications:', error);
      return {
        success: false,
        error: error.message,
        verifications: []
      };
    }
  }

  /**
   * Get response rate metrics for a specific location over the last 90 days
   * @param {string} accessToken - optional Google OAuth access token
   * @param {string} locationId - Location ID (numeric part only)
   * @param {Object} dateRange - Date range object with startDate and endDate
   * @returns {Promise<Object>} Response rate metrics data
   */
  async getResponseRateMetrics(accessToken, locationId, dateRange = null) {
    try {
      if (!locationId) {
        throw new Error('Location ID is required');
      }

      // Ensure we have a valid access token
      const token = await this._getAccessToken(accessToken);
      if (!token) {
        throw new Error('No valid access token available. Please re-authenticate with Google My Business.');
      }

      // Default to last 90 days if no date range provided
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 90);
      const defaultDateRange = dateRange || {
        startDate: {
          year: start.getFullYear(),
          month: start.getMonth() + 1,
          day: start.getDate()
        },
        endDate: {
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          day: now.getDate()
        }
      };

      // Response rate related metrics
      const metrics = [
        'BUSINESS_CONVERSATIONS',
        'BUSINESS_DIRECTION_REQUESTS',
        'CALL_CLICKS'
      ];

      const params = new URLSearchParams();
      metrics.forEach(metric => params.append('dailyMetrics', metric));
      params.append('dailyRange.startDate.year', defaultDateRange.startDate.year);
      params.append('dailyRange.startDate.month', defaultDateRange.startDate.month);
      params.append('dailyRange.startDate.day', defaultDateRange.startDate.day);
      params.append('dailyRange.endDate.year', defaultDateRange.endDate.year);
      params.append('dailyRange.endDate.month', defaultDateRange.endDate.month);
      params.append('dailyRange.endDate.day', defaultDateRange.endDate.day);

      const url = `https://businessprofileperformance.googleapis.com/v1/locations/${locationId}:fetchMultiDailyMetricsTimeSeries?${params.toString()}`;

      logger.log(`[GMB Service] Fetching response rate metrics from: ${url}`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 seconds timeout

      const response = await this._googleFetch(
        url,
        { method: 'GET', signal: controller.signal },
        token
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.warn('[GMB Service] Response rate metrics fetch failed:', {
          status: response.status,
          error: errorData
        });
        return { responseRate: 0 };
      }

      const data = await response.json();
      logger.log('[GMB Service] Response rate metrics response:', data);

      // Calculate response rate from the metrics
      // This is a simplified calculation - you may need to adjust based on actual API response structure
      let totalConversations = 0;
      let totalResponses = 0;

      if (data.multiDailyMetricTimeSeries) {
        data.multiDailyMetricTimeSeries.forEach(series => {
          if (series.dailyMetricTimeSeries) {
            series.dailyMetricTimeSeries.forEach(dailySeries => {
              if (dailySeries.dailyMetrics) {
                dailySeries.dailyMetrics.forEach(metric => {
                  if (metric.metric === 'BUSINESS_CONVERSATIONS') {
                    totalConversations += parseInt(metric.value || 0);
                  }
                  // Add other response-related metrics as needed
                });
              }
            });
          }
        });
      }

      // Calculate response rate percentage
      const responseRate = totalConversations > 0 ? Math.min(100, (totalResponses / totalConversations) * 100) : 0;

      return {
        responseRate: responseRate,
        totalConversations: totalConversations,
        totalResponses: totalResponses,
        dateRange: defaultDateRange,
        raw: data
      };

    } catch (error) {
      logger.error('Error fetching response rate metrics:', error);
      // Return default values instead of throwing to prevent breaking the scoring
      return { responseRate: 0 };
    }
  }

  /**
   * Compute response rate using live reviews data over the last `days`.
   * Mirrors logic in BusinessProfile.jsx: percentage of reviews in window that have a reply.
   * @param {string} accessToken
   * @param {string} accountName e.g. 'accounts/12345'
   * @param {string} locationName e.g. 'locations/67890'
   * @param {number} days default 90
   * @returns {Promise<{responseRate:number, counted:number, replied:number, days:number}>}
   */
  async getResponseRateFromReviews(accessToken, accountName, locationName, days = 90) {
    try {
      if (!accountName || !locationName) throw new Error('accountName and locationName are required');

      // Fetch all reviews (paginated internally)
      const reviewsResp = await this.getReviews(accessToken, accountName, locationName);
      const arr = Array.isArray(reviewsResp?.reviews)
        ? reviewsResp.reviews
        : (Array.isArray(reviewsResp) ? reviewsResp : []);

      const now = Date.now();
      const windowMs = days * 24 * 60 * 60 * 1000;
      const getTime = (r) => {
        const t = r?.updateTime || r?.createTime;
        const ms = t ? Date.parse(t) : NaN;
        return Number.isFinite(ms) ? ms : undefined;
      };
      const hasReply = (r) => !!(r?.reviewReply && (r.reviewReply.comment || r.reviewReply.updateTime));

      const windowed = arr.filter(r => {
        const ms = getTime(r);
        return ms !== undefined && (now - ms <= windowMs);
      });

      const counted = windowed.length;
      const replied = windowed.filter(hasReply).length;
      const responseRate = counted > 0 ? (replied / counted) * 100 : 0;

      logger.log('[GMBService] Computed response rate from live reviews', {
        days,
        counted,
        replied,
        responseRate: Number(responseRate.toFixed(2))
      });

      return {
        responseRate,
        counted,
        replied,
        days
      };
    } catch (error) {
      logger.warn('[GMBService] getResponseRateFromReviews failed:', error);
      return { responseRate: 0, counted: 0, replied: 0, days };
    }
  }

  /**
   * Get place action links for a specific location
   * Uses Place Actions API v1 to fetch appointment booking and other action links
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Array>} Array of place action links
   */
  async getPlaceActionLinks(locationNameOrId, accessToken) {
    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');

      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      const url = `https://mybusinessplaceactions.googleapis.com/v1/${locName}/placeActionLinks`;

      logger.log(`[GMBService] Fetching place action links for: ${locName}`);

      const response = await this._googleFetch(url, { method: 'GET' }, accessToken);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.warn('Error fetching place action links:', errorData.error?.message || 'Unknown error');
        return [];
      }

      const data = await response.json();
      logger.log('[GMBService] Place action links response:', data);

      // Extract place action links from the response
      return data.placeActionLinks || [];
    } catch (error) {
      logger.error('[GMBService] Error fetching place action links:', error);
      return [];
    }
  }

  /**
   * Get predefined service types for a specific category from Google
   * Uses the categories.batchGet API to fetch structured services
   * @param {string} categoryId - Category ID (e.g., 'gcid:electrician', 'gcid:hair_salon')
   * @param {string} regionCode - Region code (default: 'US')
   * @param {string} languageCode - Language code (default: 'en')
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Array>} Array of structured service types with serviceTypeId and displayName
   */
  async getStructuredServiceTypes(categoryId, regionCode = 'US', languageCode = 'en', accessToken) {
    try {
      if (!categoryId) throw new Error('categoryId is required');

      const url = `https://mybusinessbusinessinformation.googleapis.com/v1/categories:batchGet?regionCode=${regionCode}&languageCode=${languageCode}&names=${encodeURIComponent(categoryId)}&view=FULL`;

      logger.log(`[GMBService] Fetching structured services for category: ${categoryId}`);

      const response = await this._googleFetch(url, { method: 'GET' }, accessToken);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.warn('Error fetching structured services:', errorData.error?.message || 'Unknown error');
        return [];
      }

      const data = await response.json();
      logger.log('[GMBService] Structured services response:', data);

      const categories = data?.categories || [];
      if (categories.length === 0) return [];

      const serviceTypes = categories[0]?.serviceTypes || [];
      return serviceTypes.map(st => ({
        serviceTypeId: st.serviceTypeId,
        displayName: st.displayName,
        isStructured: true
      }));
    } catch (error) {
      logger.error('[GMBService] Error fetching structured services:', error);
      return [];
    }
  }

  /**
   * Search for categories by name to get their category IDs and services
   * Uses the categories.list API
   * @param {string} searchName - Category name to search for (e.g., 'salon', 'electrician')
   * @param {string} regionCode - Region code (default: 'US')
   * @param {string} languageCode - Language code (default: 'en')
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Array>} Array of categories with their service types
   */
  async searchCategoriesWithServices(searchName, regionCode = 'US', languageCode = 'en', accessToken) {
    try {
      if (!searchName) throw new Error('searchName is required');

      const url = `https://mybusinessbusinessinformation.googleapis.com/v1/categories?regionCode=${regionCode}&languageCode=${languageCode}&filter=displayName=${encodeURIComponent(searchName)}&view=FULL`;

      logger.log(`[GMBService] Searching categories for: ${searchName}`);

      const response = await this._googleFetch(url, { method: 'GET' }, accessToken);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.warn('Error searching categories:', errorData.error?.message || 'Unknown error');
        return [];
      }

      const data = await response.json();
      logger.log('[GMBService] Category search response:', data);

      const categories = data?.categories || [];
      return categories.map(cat => ({
        categoryId: cat.name,
        displayName: cat.displayName,
        serviceTypes: (cat.serviceTypes || []).map(st => ({
          serviceTypeId: st.serviceTypeId,
          displayName: st.displayName,
          isStructured: true
        }))
      }));
    } catch (error) {
      logger.error('[GMBService] Error searching categories:', error);
      return [];
    }
  }

  /**
   * Get service items with descriptions for a specific location
   * Uses Business Information API v1 with serviceItems readMask
   * @param {string} locationNameOrId - e.g., 'locations/1234567890' or just the numeric ID
   * @param {string} accessToken - optional Google OAuth access token
   * @returns {Promise<Array>} Array of service items with descriptions
   */
  async getServiceItems(locationNameOrId, accessToken) {
    const transformServiceItems = (serviceItems = []) => {
      const cleanServiceId = (raw) => {
        if (!raw) return '';
        const withoutPrefix = String(raw).replace(/^job[_\s-]?type[_\s-]?id:\s*/i, '').replace(/^job_type_id:/i, '');
        const spaced = withoutPrefix.replace(/_/g, ' ').trim();
        return spaced.replace(/\b\w+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
      };

      return serviceItems.map((item, index) => {
        const structured = item.structuredServiceItem;
        const freeForm = item.freeFormServiceItem;
        const labelInfo = freeForm?.label;
        const rawStructuredId = structured?.serviceTypeId || null;
        const rawServiceId = item.serviceId || item.serviceTypeId || null;
        const idSource = rawStructuredId || rawServiceId;
        const cleanedId = cleanServiceId(idSource);
        const displayName = labelInfo?.displayName || cleanedId || `Service ${index + 1}`;
        const description = structured?.description
          || labelInfo?.description
          || item.description
          || 'No description available';
        const priceSource = structured?.price || item.price;
        const price = priceSource
          ? `${priceSource.currencyCode || ''} ${priceSource.units || priceSource.amount || ''}`.trim()
          : null;
        const category = structured?.category || item.category || null;

        return {
          id: idSource ? (cleanedId || idSource) : `service_${index}`,
          name: displayName,
          description,
          price,
          category,
          serviceTypeId: rawStructuredId || item.serviceTypeId || null,
          serviceId: rawServiceId,
          languageCode: labelInfo?.languageCode || null,
          rawServiceItem: item,
          structured: Boolean(structured),
          freeForm: Boolean(freeForm)
        };
      });
    };

    try {
      if (!locationNameOrId) throw new Error('locationNameOrId is required');

      const locName = String(locationNameOrId).includes('locations/')
        ? String(locationNameOrId)
        : `locations/${locationNameOrId}`;

      const adminViewingUserId = this._resolveAdminViewingUserId();
      const encodedLocation = encodeURIComponent(locName);
      const queryParts = [];
      if (adminViewingUserId) {
        queryParts.push(`userId=${encodeURIComponent(adminViewingUserId)}`);
      }
      queryParts.push('readMask=serviceItems');
      const backendEndpoint = `/gmb/location/${encodedLocation}${queryParts.length ? `?${queryParts.join('&')}` : ''}`;

      logger.log(`[GMBService] Fetching service items for: ${locName}`);

      const token = await this._getAccessToken(accessToken);
      let data;

      // When admin is viewing a client, ALWAYS use backend proxy to ensure correct token
      if (adminViewingUserId) {
        logger.log(`[GMBService] Admin viewing user ${adminViewingUserId}, using backend proxy for service items`);
        const ApiService = (await import('./api')).default;
        const resp = await ApiService.get(backendEndpoint);
        data = resp?.data?.location || resp?.location || resp || {};
      } else if (!token) {
        logger.log('[GMBService] No access token available, using backend proxy for service items');
        const ApiService = (await import('./api')).default;
        const resp = await ApiService.get(backendEndpoint);
        data = resp?.data?.location || resp?.location || resp || {};
      } else {
        const url = `https://mybusinessbusinessinformation.googleapis.com/v1/${locName}?readMask=serviceItems`;
        const response = await this._googleFetch(url, { method: 'GET' }, accessToken);

        if (!response.ok) {
          if (response.status === 401) {
            logger.warn('[GMBService] Direct Google call unauthorized, falling back to backend proxy for service items');
            const ApiService = (await import('./api')).default;
            const resp = await ApiService.get(backendEndpoint);
            data = resp?.data?.location || resp?.location || resp || {};
          } else {
            const errorData = await response.json().catch(() => ({}));
            logger.warn('Error fetching service items:', errorData.error?.message || 'Unknown error');
            return [];
          }
        } else {
          data = await response.json();
        }
      }

      logger.log('[GMBService] Service items response:', data);
      const serviceItems = Array.isArray(data?.serviceItems) ? data.serviceItems : [];
      return transformServiceItems(serviceItems);
    } catch (error) {
      logger.error('[GMBService] Error fetching service items:', error);
      return [];
    }
  }

}

export default new GMBService();
